import { FC, useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { KTSVG } from '../../../../_metronic/helpers'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {
  getRFQsPage,
  getQuotationsByRFQPage,
  createRFQFromRequisition,
  updateRFQ,
  inviteVendorsToRFQ,
  inviteVendorsToRFQLines,
  sendRFQ,
  deleteRFQ,
  getRfqVendorCandidates,
  getRequisitions,
  getVendors,
  getPorts,
  getAllVessels,
  getInventoryCategoriesForResolvedTenant,
  getVendorCategories,
  getRFQById,
  getRequisitionById,
  searchRegions,
  listVendorLogisticsChangeRequests,
  approveVendorLogisticsChangeRequest,
  rejectVendorLogisticsChangeRequest,
  VendorLogisticsChangeRequest,
} from '../core/_requests'
import {
  RFQDisplay,
  CreateRFQFromRequisitionRequest,
  UpdateRFQRequest,
  RFQ,
  RFQListSummary,
  Requisition,
  Vendor,
  Port,
  RegionOption,
  RfqVendorCandidatesResponse,
} from '../core/_models'
import { useAuth } from '../../auth'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import { useProcurementPermissions } from '../core/permissionAccess'
import { PROCUREMENT_PERMS } from '../core/permissions'
import ProcurementFilterPanel, { FilterOption } from './ProcurementFilterPanel'
import ProcurementDocumentPreviewModal from './ProcurementDocumentPreviewModal'
import QuotationDPCDocument from './QuotationDPCDocument'
import { downloadElementAsPdf } from '../core/exportUtils'
import ProcurementExportModal, {
  ProcurementExportRecord,
  ProcurementExportSelection,
} from './ProcurementExportModal'
import VendorLogisticsChangeRequestModal from './VendorLogisticsChangeRequestModal'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import ProcurementActionGrid from './ProcurementActionGrid'
import VendorQuotationEditorCard from './VendorQuotationEditorCard'
import MoneyDisplay from './MoneyDisplay'
import {formatExchangeRateDisplay} from '../core/currencyConversion'

const groupVendorLogisticsRequestsByRfqId = (
  requests: VendorLogisticsChangeRequest[]
): Map<number, VendorLogisticsChangeRequest[]> => {
  const grouped = new Map<number, VendorLogisticsChangeRequest[]>()
  requests.forEach((request) => {
    const rfqId = Number(request.rfqId)
    if (!Number.isFinite(rfqId) || rfqId <= 0) return
    const existing = grouped.get(rfqId) || []
    existing.push(request)
    grouped.set(rfqId, existing)
  })
  return grouped
}

const pickFirstText = (...values: any[]): string => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim()
    }
  }
  return ''
}

const resolveVesselName = (payload: any, fallbackVesselId?: number | string | null): string => {
  const name = pickFirstText(
    payload?.vesselName,
    payload?.vessel?.fleet_name,
    payload?.vessel?.fleetName,
    payload?.vessel?.name,
    payload?.vessel?.vesselName,
    payload?.category?.vesselName
  )
  if (name) return name
  if (fallbackVesselId !== null && fallbackVesselId !== undefined && String(fallbackVesselId).trim() !== '') {
    return `Vessel ${fallbackVesselId}`
  }
  return 'N/A'
}

const resolveInvitationVendorId = (detail: any): number | null => {
  const direct = Number(detail?.vendorId)
  if (Number.isFinite(direct) && direct > 0) return direct
  const nested = Number(detail?.vendor?.id)
  if (Number.isFinite(nested) && nested > 0) return nested
  return null
}

const resolveInvitationVendorName = (detail: any): string => {
  return pickFirstText(detail?.vendorName, detail?.vendor?.name)
}

const resolveInvitationStatus = (detail: any): string => {
  return String(detail?.invitationStatus || detail?.status || '').trim().toUpperCase()
}

const resolveInvitationLineId = (detail: any): number | null => {
  const direct = Number(detail?.requisitionLineId)
  if (Number.isFinite(direct) && direct > 0) return direct
  const nested = Number(detail?.requisitionLine?.id)
  if (Number.isFinite(nested) && nested > 0) return nested
  return null
}

const resolveVendorMappingId = (vendor: Partial<Vendor>): number => {
  const value = Number(vendor.vendorCompanyMappingId ?? vendor.id ?? 0)
  return Number.isFinite(value) ? value : 0
}

const emptyVendorCandidates: RfqVendorCandidatesResponse = {
  suggestedVendors: [],
  otherVendors: [],
  regionMatched: false,
}

const VendorPerformanceHint = ({vendor, category}: {vendor: Vendor; category?: string | null}) => {
  const categoryName = (category || '').trim().toLowerCase()
  const metric = (vendor.categoryPerformance || []).find((item) =>
    item.categoryName?.trim().toLowerCase() === categoryName
  )
  if (!metric) return <small className='d-block text-muted'>No delivery history</small>
  return <small className='d-block text-muted'>
    {metric.rating == null ? 'Not rated' : `Rating ${metric.rating.toFixed(1)}/5`} · {metric.totalCompletedOrders} orders · {metric.averageDeliveryDays == null ? 'n/a' : `${metric.averageDeliveryDays.toFixed(1)} days`} · {metric.issueItemCount} issues
  </small>
}

interface CreateRFQModalProps {
  visible: boolean
  onClose: () => void
  onSubmit: () => void
  requisitions: Requisition[]
  vendors: Vendor[]
  ports: Port[]
  filterVessels?: Array<{ id: number; name: string }>
  mode?: 'normal' | 'from-requisition'
  requisitionId?: number | null
}

const CreateRFQModal: FC<CreateRFQModalProps> = ({
  visible,
  onClose,
  onSubmit,
  requisitions,
  vendors,
  ports,
  filterVessels,
  mode = 'normal',
  requisitionId: quickCreateRequisitionId = null,
}) => {
  const isQuickCreate = mode === 'from-requisition'
  const modalVesselOptions = useMemo(() => {
    const map = new Map<number, string>()
    ;(filterVessels || []).forEach((v) => {
      if (v.id && v.name) map.set(Number(v.id), v.name)
    })
    ;(requisitions || []).forEach((req) => {
      const rawVId = req.vesselId ?? (req as any).vessel?.id ?? (req as any).vessel_id
      if (rawVId != null && rawVId !== '') {
        const vId = Number(rawVId)
        if (Number.isFinite(vId) && vId > 0 && !map.has(vId)) {
          const vName = resolveVesselName(req, vId)
          if (vName && vName !== 'N/A') map.set(vId, vName)
        }
      }
    })
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]))
  }, [filterVessels, requisitions])
  const [formData, setFormData] = useState({
    vesselId: '',
    requisitionId: '',
    title: '',
    responseDueDate: '',
    deliveryPortId: '',
    selectedVendors: [] as number[],
    lineVendorSelection: {} as { [lineId: number]: number[] }
  })
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasMultipleLines, setHasMultipleLines] = useState(false)
  const [createSeparateRFQs, setCreateSeparateRFQs] = useState(false)
  const [selectedRequisitionDetail, setSelectedRequisitionDetail] = useState<Requisition | null>(null)
  const [vendorSearchTerm, setVendorSearchTerm] = useState('')
  const [vendorCandidates, setVendorCandidates] = useState<RfqVendorCandidatesResponse>(emptyVendorCandidates)
  const getLineSelectionKey = (line: { id?: number | null }, index: number): number =>
    line.id != null ? line.id : -(index + 1)

  useEffect(() => {
    if (visible) {
      // Check for URL parameter first
      const urlParams = new URLSearchParams(window.location.search)
      const requisitionId = isQuickCreate && quickCreateRequisitionId
        ? String(quickCreateRequisitionId)
        : urlParams.get('requisitionId')

      if (requisitionId && !formData.requisitionId) {
        // Pre-select the requisition from URL
        setFormData(prev => ({
          ...prev,
          requisitionId: requisitionId
        }))
      }

      if (formData.requisitionId) {
        const req = selectedRequisitionDetail || requisitions.find(r => r.id === parseInt(formData.requisitionId))
        if (req) {
          const hasLines = !!(req.lines && req.lines.length > 0)
          setHasMultipleLines(hasLines)

          // Initialize line vendor selection if has lines
          if (hasLines) {
            const lineVendorSelection: { [lineId: number]: number[] } = {}
            req.lines!.forEach((line, index) => {
              const lineKey = getLineSelectionKey(line, index)
              lineVendorSelection[lineKey] = []
            })
            setFormData(prev => ({
              ...prev,
              vesselId: req.vesselId ? String(req.vesselId) : prev.vesselId,
              title: `RFQ - ${req.title}`,
              deliveryPortId: req.requestedPortId?.toString() || '',
                        lineVendorSelection
            }))
          } else {
            setFormData(prev => ({
              ...prev,
              vesselId: req.vesselId ? String(req.vesselId) : prev.vesselId,
              title: `RFQ - ${req.title}`,
              deliveryPortId: req.requestedPortId?.toString() || '',
                      }))
          }
        }
      }
    }
  }, [formData.requisitionId, requisitions, selectedRequisitionDetail, visible, isQuickCreate, quickCreateRequisitionId])

  useEffect(() => {
    if (!visible || !formData.requisitionId) {
      setSelectedRequisitionDetail(null)
      return
    }
    let active = true
    ;(async () => {
      try {
        const detail = await getRequisitionById(Number(formData.requisitionId))
        if (active) {
          setSelectedRequisitionDetail(detail)
        }
      } catch (error) {
        console.error('Failed to load requisition detail for RFQ create flow', error)
        if (active) {
          setSelectedRequisitionDetail(null)
        }
      }
    })()
    return () => {
      active = false
    }
  }, [formData.requisitionId, visible])

  useEffect(() => {
    if (!visible || !formData.requisitionId) {
      return
    }
    let active = true
    const handle = window.setTimeout(async () => {
      try {
        const response = await getRfqVendorCandidates(
          Number(formData.requisitionId),
          null,
          vendorSearchTerm
        )
        if (active) {
          setVendorCandidates(response)
        }
      } catch (error) {
        console.error('Failed to load RFQ vendor candidates', error)
        if (active) {
          setVendorCandidates(emptyVendorCandidates)
        }
      }
    }, 250)
    return () => {
      active = false
      window.clearTimeout(handle)
    }
  }, [visible, formData.requisitionId, vendorSearchTerm])

  const suggestedVendors = vendorCandidates.suggestedVendors || []
  const otherVendors = vendorCandidates.otherVendors || []

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target

    // Requisition-dependent context must never survive a requisition switch.
    // Clear it before the detail/candidate requests for the new requisition run.
    if (name === 'requisitionId') {
      setSelectedRequisitionDetail(null)
      setHasMultipleLines(false)
      setVendorSearchTerm('')
      setVendorCandidates(emptyVendorCandidates)
      setFormData(prev => ({
        ...prev,
        requisitionId: value,
        title: '',
        deliveryPortId: '',
        selectedVendors: [],
        lineVendorSelection: {},
      }))
      if (errors.requisitionId) {
        setErrors(prev => ({
          ...prev,
          requisitionId: '',
        }))
      }
      return
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const handleVendorToggle = (vendorId: number) => {
    setFormData(prev => ({
      ...prev,
      selectedVendors: prev.selectedVendors.includes(vendorId)
        ? prev.selectedVendors.filter(id => id !== vendorId)
        : [...prev.selectedVendors, vendorId]
    }))
  }

  const handleLineVendorToggle = (lineId: number, vendorId: number) => {
    setFormData(prev => {
      const currentLineVendors = prev.lineVendorSelection[lineId] || []
      const newLineVendors = currentLineVendors.includes(vendorId)
        ? currentLineVendors.filter(id => id !== vendorId)
        : [...currentLineVendors, vendorId]

      return {
        ...prev,
        lineVendorSelection: {
          ...prev.lineVendorSelection,
          [lineId]: newLineVendors
        }
      }
    })
  }

  const validateStep1 = () => {
    const newErrors: { [key: string]: string } = {}

    if (!formData.requisitionId) {
      newErrors.requisitionId = 'Please select a requisition'
    }
    if (!formData.title.trim()) newErrors.title = 'RFQ Title is required'
    if (!formData.responseDueDate) newErrors.responseDueDate = 'Response Due Date is required'
    if (formData.responseDueDate) {
      const dueDate = new Date(formData.responseDueDate)
      const dueDateOnly = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate())
      const today = new Date()
      const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate())
      if (dueDateOnly < todayOnly) {
        newErrors.responseDueDate = 'Response Due Date cannot be in the past'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const validateStep2 = () => {
    const newErrors: { [key: string]: string } = {}

    if (hasMultipleLines && createSeparateRFQs) {
      // Validate that each line has at least one vendor selected
      const selectedReq =
        selectedRequisitionDetail || requisitions.find(r => r.id === parseInt(formData.requisitionId))
      if (selectedReq && selectedReq.lines) {
        const hasEmptyLines = selectedReq.lines.some((line, index) =>
          !formData.lineVendorSelection[getLineSelectionKey(line, index)] ||
          formData.lineVendorSelection[getLineSelectionKey(line, index)].length === 0
        )
        if (hasEmptyLines) {
          newErrors.selectedVendors = 'Please select at least one vendor for each subcomponent'
        }
      }
    } else {
      if (formData.selectedVendors.length === 0) {
        newErrors.selectedVendors = 'Please select at least one vendor'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateStep1() || !validateStep2()) return

    setIsSubmitting(true)
    try {
      const requisitionId = parseInt(formData.requisitionId)
      let selectedReq = selectedRequisitionDetail || requisitions.find(r => r.id === requisitionId)
      if (!selectedReq || selectedReq.lines?.some((line) => line.id == null)) {
        selectedReq = await getRequisitionById(requisitionId)
      }

      if (!selectedReq) {
        toast.error('Selected requisition not found')
        return
      }

      // Check if requisition has lines
      const hasLines = selectedReq.lines && selectedReq.lines.length > 0
      console.log('Requisition has lines:', hasLines, 'Lines:', selectedReq.lines)

      // Use requisition's company IDs for the RFQ (no hardcoded fallback)
      const requisitionCgaid = selectedReq.category?.cgaid?.id
      const requisitionCaid = selectedReq.category?.caid?.id ?? null
      if (!requisitionCgaid) {
        toast.error('Selected requisition is missing company group admin (cgaid)')
        return
      }
      const cgaid = requisitionCgaid
      const caid = requisitionCaid
      console.log('Creating RFQ with requisition cgaid:', cgaid, 'caid:', caid)

      if (hasLines && createSeparateRFQs) {
        // Line-wise vendor assignment mode: create ONE RFQ and invite vendors per line.
        const lineInvitations: Array<{ requisitionLineId: number; vendorCompanyMappingIds: number[] }> = []
        const unresolvedLineIndexes: number[] = []
        selectedReq.lines!.forEach((line, index) => {
          const lineKey = getLineSelectionKey(line, index)
          const lineVendors = formData.lineVendorSelection[lineKey] || []
          if (lineVendors.length === 0) return
          if (line.id == null) {
            unresolvedLineIndexes.push(index + 1)
            return
          }
          const uniqueVendorIds = Array.from(new Set(lineVendors))
          lineInvitations.push({
            requisitionLineId: line.id as number,
            vendorCompanyMappingIds: uniqueVendorIds,
          })
        })

        if (unresolvedLineIndexes.length > 0) {
          throw new Error(
            `Line mapping missing for line(s): ${unresolvedLineIndexes.join(', ')}. Please reload requisition and retry.`
          )
        }

        if (lineInvitations.length === 0) {
          toast.error('No valid vendor-line assignments found for separate RFQ mode')
          return
        }

        const payload: CreateRFQFromRequisitionRequest = {
          title: formData.title,
          responseDueDate: formData.responseDueDate,
          cgaid,
          caid,
          deliveryPortOverrideId: formData.deliveryPortId ? parseInt(formData.deliveryPortId) : null,
        }

        const rfq = await createRFQFromRequisition(requisitionId, payload)
        await inviteVendorsToRFQLines(rfq.id, lineInvitations)

        const invitedVendorCount = new Set(lineInvitations.flatMap((li) => li.vendorCompanyMappingIds)).size
        toast.success(
          `RFQ created successfully with line-wise invitations for ${invitedVendorCount} vendor(s).`
        )
      } else {
        // Original behavior: Create one RFQ for all lines or single item
        const payload: CreateRFQFromRequisitionRequest = {
          title: formData.title,
          responseDueDate: formData.responseDueDate,
          cgaid: cgaid,
          caid: caid,
          deliveryPortOverrideId: formData.deliveryPortId ? parseInt(formData.deliveryPortId) : null,
        }

        console.log('Creating RFQ with payload:', payload)
        const rfq = await createRFQFromRequisition(requisitionId, payload)
        console.log('RFQ created:', rfq)

        // Invite vendors based on whether requisition has lines
        let updatedRfq: RFQ

        if (hasLines) {
          // If requisition has lines, invite vendors for each line
          console.log('Requisition has lines, inviting vendors per line')
          const lineInvitations = selectedReq.lines!
            .filter((line) => line.id != null)
            .map(line => ({
              requisitionLineId: line.id as number,
              vendorCompanyMappingIds: formData.selectedVendors
            }))

          if (lineInvitations.length !== selectedReq.lines!.length) {
            throw new Error('Some requisition lines are missing IDs. Please reload and retry.')
          }

          console.log('Line invitations:', lineInvitations)
          updatedRfq = await inviteVendorsToRFQLines(rfq.id, lineInvitations)
          console.log('Vendors invited to lines:', updatedRfq)
        } else {
          // If no lines, invite vendors to the RFQ directly (old behavior)
          console.log('Requisition has no lines, inviting vendors to RFQ')
          updatedRfq = await inviteVendorsToRFQ(rfq.id, formData.selectedVendors)
          console.log('Vendors invited:', formData.selectedVendors, 'Updated RFQ:', updatedRfq)
        }

        console.log('RFQ created with status:', updatedRfq.status)
        console.log('Invited vendors:', updatedRfq.invitedVendorIds)

        toast.success('RFQ created and vendors invited successfully!')
      }

      handleClose()

      // Refresh list, but do not convert post-success refresh failure into create failure.
      try {
        await onSubmit()
      } catch (refreshError) {
        console.error('RFQ created, but refresh failed:', refreshError)
        toast.info('RFQ creation succeeded, but list refresh failed. Please refresh once.')
      }

      // Clear URL parameters after successful creation
      window.history.replaceState({}, document.title, window.location.pathname)
    } catch (error: any) {
      console.error('Error creating RFQ:', error)
      toast.error('Failed to create RFQ')
    } finally {
      setIsSubmitting(false)
    }
  }

  const resetForm = () => {
    setFormData({
      vesselId: '',
      requisitionId: '',
      title: '',
          responseDueDate: '',
      deliveryPortId: '',
        selectedVendors: [],
      lineVendorSelection: {}
    })
    setErrors({})
    setHasMultipleLines(false)
    setCreateSeparateRFQs(false)
    setVendorSearchTerm('')
    setVendorCandidates(emptyVendorCandidates)
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  if (!visible) return null

  return (
    <div
      className="modal fade show d-flex align-items-center justify-content-center procurement-document-modal"
      tabIndex={-1}
      style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1050,
      }}
    >
      <div className='modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable' role='document'>
        <div className='modal-content bg-white' style={{ color: '#181C32' }}>
          <div className='modal-header'>
            <div>
              <h5 className='modal-title'>Create New RFQ</h5>
            </div>
            <button type='button' className='btn-close' onClick={handleClose}></button>
          </div>

          <div className='modal-body' style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <>
              <div className='row g-3'>
                <div className='col-md-6'>
                  <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>
                    {isQuickCreate ? 'Vessel' : 'Vessel Filter'}
                  </label>
                  {isQuickCreate ? (
                    <input
                      type='text'
                      className='form-control'
                      value={resolveVesselName(selectedRequisitionDetail, formData.vesselId)}
                      readOnly
                    />
                  ) : (
                    <SearchableSelect
                      className='form-select'
                      name='vesselId'
                      value={formData.vesselId}
                      onChange={handleInputChange}
                    >
                      <option value=''>All permitted vessels</option>
                      {modalVesselOptions.map(([vId, vesselName]) => (
                        <option key={vId} value={vId}>{vesselName}</option>
                      ))}
                    </SearchableSelect>
                  )}
                </div>

                <div className='col-md-12'>
                  <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Select Requisition</label>
                  {isQuickCreate ? (
                    <input
                      type='text'
                      className={`form-control ${errors.requisitionId ? 'is-invalid' : ''}`}
                      value={selectedRequisitionDetail
                        ? `${selectedRequisitionDetail.requisition_code} - ${selectedRequisitionDetail.name || selectedRequisitionDetail.title}`
                        : formData.requisitionId}
                      readOnly
                    />
                  ) : (
                    <SearchableSelect
                      className={`form-select ${errors.requisitionId ? 'is-invalid' : ''}`}
                      name='requisitionId'
                      value={formData.requisitionId}
                      onChange={handleInputChange}
                    >
                      <option value=''>Select a requisition</option>
                      {requisitions
                        .filter(req => {
                          const status = String(req.status || '').toUpperCase()
                          return status === 'SHORE_APPROVED'
                        })
                        .filter(req => {
                          if (!formData.vesselId) return true
                          const reqVesselId = Number(req.vesselId ?? (req as any).vessel?.id ?? (req as any).vessel_id)
                          return reqVesselId === Number(formData.vesselId)
                        })
                        .map(req => (
                          <option key={req.id} value={req.id}>
                            {req.requisition_code} - {(req.name || req.title)} ({req.status})
                          </option>
                        ))}
                    </SearchableSelect>
                  )}
                  {errors.requisitionId && <div className='invalid-feedback'>{errors.requisitionId}</div>}
                </div>

                <div className='col-md-12'>
                  <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>RFQ Title</label>
                  <input
                    type='text'
                    className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                    name='title'
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder='RFQ title (auto-generated from requisition)'
                    style={{ color: '#000' }}
                  />
                  {errors.title && <div className='invalid-feedback'>{errors.title}</div>}
                </div>

                <div className='col-md-6'>
                  <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Response Due Date</label>
                  <input
                    type='date'
                    className={`form-control ${errors.responseDueDate ? 'is-invalid' : ''}`}
                    name='responseDueDate'
                    value={formData.responseDueDate}
                    min={new Date().toISOString().split('T')[0]}
                    onChange={handleInputChange}
                    style={{ color: '#000' }}
                  />
                  {errors.responseDueDate && <div className='invalid-feedback'>{errors.responseDueDate}</div>}
                </div>

                <div className='col-md-6'>
                  <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>
                    Delivery Port (Optional Override)
                  </label>
                  <SearchableSelect
                    className={`form-select ${errors.deliveryPortId ? 'is-invalid' : ''}`}
                    name='deliveryPortId'
                    value={formData.deliveryPortId}
                    onChange={handleInputChange}
                  >
                    <option value=''>Use requisition port</option>
                    {ports.map(port => (
                      <option key={port.id} value={port.id}>
                        {port.name || port.main_port}{port.code ? ` (${port.code})` : ''}
                      </option>
                    ))}
                  </SearchableSelect>
                  {errors.deliveryPortId && <div className='invalid-feedback'>{errors.deliveryPortId}</div>}
                </div>

              </div>
              <div>
                <h6 className='fw-bold mb-3'>Select Vendors to Invite (3-5 recommended)</h6>

                {hasMultipleLines && (
                  <div className='alert alert-info mb-4'>
                    <div className='form-check'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        id='createSeparateRFQs'
                        checked={createSeparateRFQs}
                        onChange={(e) => setCreateSeparateRFQs(e.target.checked)}
                      />
                      <label className='form-check-label' htmlFor='createSeparateRFQs'>
                        Create separate RFQs for each subcomponent with different vendors
                      </label>
                    </div>
                    <small className='text-muted d-block mt-2'>
                      This requisition has multiple subcomponents. Enable this option to select different vendors for each subcomponent.
                    </small>
                  </div>
                )}

                {errors.selectedVendors && (
                  <div className='alert alert-danger'>{errors.selectedVendors}</div>
                )}

                <div className='row g-3 mb-4'>
                  <div className='col-md-12'>
                    <label className='form-label fw-semibold fs-7 mb-2' style={{ color: '#181C32' }}>
                      Search vendor
                    </label>
                    <input
                      type='text'
                      className='form-control'
                      value={vendorSearchTerm}
                      onChange={(event) => setVendorSearchTerm(event.target.value)}
                      placeholder='Type vendor name, code, email, or contact'
                    />
                  </div>
                </div>

                {hasMultipleLines && createSeparateRFQs ? (
                  // Show vendor selection per line
                  <div>
                    {selectedRequisitionDetail?.lines?.map((line, lineIndex) => {
                      const lineKey = getLineSelectionKey(line, lineIndex)
                      const lineAny = line as any
                      const subcomponentLabel =
                        lineAny?.subcomponent?.name ||
                        lineAny?.subcomponentName ||
                        (line.subcomponentId ? `Subcomponent ${line.subcomponentId}` : `Subcomponent ${lineIndex + 1}`)
                      const partLabel =
                        lineAny?.part?.name ||
                        lineAny?.partName ||
                        lineAny?.manualItemName ||
                        (line.partId ? `Part ${line.partId}` : 'Item')
                      const partCode =
                        lineAny?.part?.partNo ||
                        lineAny?.part?.code ||
                        lineAny?.partNumber ||
                        null
                      return (
                        <div key={`${line.id ?? 'line'}-${lineIndex}`} className='mb-4 border rounded p-3'>
                          <h6 className='fw-bold mb-3'>
                            {subcomponentLabel} - {partLabel}{partCode ? ` (${partCode})` : ''}
                            <span className='badge bg-secondary ms-2'>Qty: {line.quantity}</span>
                          </h6>
                          <div className='mb-3'>
                            <div className='fw-semibold mb-2'>Suggested Vendors ({suggestedVendors.length})</div>
                            <div className='row g-3'>
                              {suggestedVendors.map(vendor => {
                                const vendorKey = resolveVendorMappingId(vendor)
                                const isSelected = formData.lineVendorSelection[lineKey]?.includes(vendorKey) || false
                                return (
                                  <div key={`suggested-${vendorKey}`} className='col-md-6'>
                                    <div
                                      className={`card cursor-pointer ${isSelected ? 'border-primary' : ''}`}
                                      onClick={() => handleLineVendorToggle(lineKey, vendorKey)}
                                      style={{
                                        cursor: 'pointer',
                                        backgroundColor: isSelected ? '#F4F9FF' : 'white'
                                      }}
                                    >
                                      <div className='card-body p-2'>
                                        <div className='d-flex align-items-center'>
                                          <input
                                            type='checkbox'
                                            className='form-check-input me-2'
                                            checked={isSelected}
                                            onChange={() => { }}
                                          />
                                          <div className='flex-grow-1'>
                                            <h6 className='mb-0 fs-7 fw-bold'>{vendor.name}</h6>
                                            <small className='text-muted d-block'>{vendor.email}</small>
                                            {vendor.matchedRegion && <small className='text-success d-block'>{vendor.matchedRegion}</small>}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                          <div className='mb-3'>
                            <div className='fw-semibold mb-2'>Other Vendors ({otherVendors.length})</div>
                            <div className='row g-3'>
                              {otherVendors.map(vendor => {
                                const vendorKey = resolveVendorMappingId(vendor)
                                const isSelected = formData.lineVendorSelection[lineKey]?.includes(vendorKey) || false
                                return (
                                  <div key={`other-${vendorKey}`} className='col-md-6'>
                                    <div
                                      className={`card cursor-pointer ${isSelected ? 'border-primary' : ''}`}
                                      onClick={() => handleLineVendorToggle(lineKey, vendorKey)}
                                      style={{
                                        cursor: 'pointer',
                                        backgroundColor: isSelected ? '#F4F9FF' : 'white'
                                      }}
                                    >
                                      <div className='card-body p-2'>
                                        <div className='d-flex align-items-center'>
                                          <input
                                            type='checkbox'
                                            className='form-check-input me-2'
                                            checked={isSelected}
                                            onChange={() => { }}
                                          />
                                          <div className='flex-grow-1'>
                                            <h6 className='mb-0 fs-7 fw-bold'>{vendor.name}</h6>
                                            <small className='text-muted'>{vendor.email}</small>
                                          </div>
                                          <span className={`badge badge-sm ${vendor.tag === 'PREMIUM' || vendor.status === 'Preferred' ? 'bg-success' : 'bg-primary'}`}>
                                            {vendor.tag || vendor.status || 'NORMAL'}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                          <div className='mt-2 text-muted'>
                            <small>Selected: {formData.lineVendorSelection[lineKey]?.length || 0} vendor(s)</small>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  // Show regular vendor selection (all vendors for all lines)
                  <div>
                    <div className='mb-4'>
                      <div className='fw-semibold mb-2'>Suggested Vendors ({suggestedVendors.length})</div>
                      <div className='row g-3'>
                        {suggestedVendors.map(vendor => {
                          const vendorKey = resolveVendorMappingId(vendor)
                          return (
                            <div key={`suggested-${vendorKey}`} className='col-md-6'>
                              <div
                                className={`card cursor-pointer ${formData.selectedVendors.includes(vendorKey) ? 'border-primary' : ''}`}
                                onClick={() => handleVendorToggle(vendorKey)}
                                style={{
                                  cursor: 'pointer',
                                  backgroundColor: formData.selectedVendors.includes(vendorKey) ? '#F4F9FF' : 'white'
                                }}
                              >
                                <div className='card-body p-3'>
                                  <div className='d-flex justify-content-between align-items-start'>
                                    <div className='flex-grow-1'>
                                      <div className='d-flex align-items-center mb-2'>
                                        <input
                                          type='checkbox'
                                          className='form-check-input me-2'
                                          checked={formData.selectedVendors.includes(vendorKey)}
                                          onChange={() => { }}
                                        />
                                        <h6 className='mb-0 fw-bold'>{vendor.name}</h6>
                                      </div>
                                      <p className='text-muted mb-1 fs-7'>{vendor.email}</p>
                                      {vendor.matchedRegion && <p className='text-success mb-1 fs-7'>{vendor.matchedRegion}</p>}
                                      {vendor.matchType && <p className='text-muted mb-1 fs-7'>{vendor.matchType}</p>}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                    <div className='mb-4'>
                      <div className='fw-semibold mb-2'>Other Vendors ({otherVendors.length})</div>
                      <div className='row g-3'>
                        {otherVendors.map(vendor => {
                          const vendorKey = resolveVendorMappingId(vendor)
                          return (
                            <div key={`other-${vendorKey}`} className='col-md-6'>
                              <div
                                className={`card cursor-pointer ${formData.selectedVendors.includes(vendorKey) ? 'border-primary' : ''}`}
                                onClick={() => handleVendorToggle(vendorKey)}
                                style={{
                                  cursor: 'pointer',
                                  backgroundColor: formData.selectedVendors.includes(vendorKey) ? '#F4F9FF' : 'white'
                                }}
                              >
                                <div className='card-body p-3'>
                                  <div className='d-flex justify-content-between align-items-start'>
                                    <div className='flex-grow-1'>
                                      <div className='d-flex align-items-center mb-2'>
                                        <input
                                          type='checkbox'
                                          className='form-check-input me-2'
                                          checked={formData.selectedVendors.includes(vendorKey)}
                                          onChange={() => { }}
                                        />
                                        <h6 className='mb-0 fw-bold'>{vendor.name}</h6>
                                      </div>
                                      <p className='text-muted mb-1 fs-7'>{vendor.email}</p>
                                      {(vendor.contactPerson || vendor.contactNo) && (
                                        <p className='text-muted mb-1 fs-7'>{vendor.contactPerson || vendor.contactNo}</p>
                                      )}
                                      <div className='d-flex gap-1 flex-wrap mt-2'>
                                        {vendor.categories && vendor.categories.length > 0 ? (
                                          <>
                                            {vendor.categories.slice(0, 2).map((cat, idx) => (
                                              <span key={idx} className='badge bg-secondary'>{cat}</span>
                                            ))}
                                            {vendor.categories.length > 2 && (
                                              <span className='badge bg-secondary'>+{vendor.categories.length - 2}</span>
                                            )}
                                          </>
                                        ) : (
                                          <span className='text-muted fs-7'>No categories</span>
                                        )}
                                      </div>
                                    </div>
                                    <span className={`badge ${vendor.tag === 'PREMIUM' || vendor.status === 'Preferred' ? 'bg-success' : 'bg-primary'}`}>
                                      {vendor.tag || vendor.status || 'NORMAL'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                    {suggestedVendors.length === 0 && otherVendors.length === 0 && (
                      <p className='text-center text-muted py-5'>No vendors available. Please add vendors first.</p>
                    )}
                    <div className='mt-3 text-muted'>
                      <small>Selected: {formData.selectedVendors.length} vendor(s)</small>
                    </div>
                  </div>
                )}
              </div>
            </>
          </div>

          <div className='modal-footer'>
            <button type='button' className='btn btn-light btn-sm' onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </button>
            <button type='button' className='btn btn_primary' onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create RFQ'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

interface EditRFQModalProps {
  visible: boolean
  onClose: () => void
  onSubmit: () => void
  rfq: RFQ | null
  requisitions: Requisition[]
  vendors: Vendor[]
  ports: Port[]
}

const EditRFQModal: FC<EditRFQModalProps> = ({ visible, onClose, onSubmit, rfq, requisitions, vendors, ports }) => {
  const [formData, setFormData] = useState({
    requisitionId: '',
    requisitionLabel: '',
    title: '',
    itemDescription: '',
    technicalSpecifications: '',
    responseDueDate: '',
    deliveryPortId: '',
    selectedVendors: [] as number[],
    lineVendorSelection: {} as { [lineId: number]: number[] },
  })
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasMultipleLines, setHasMultipleLines] = useState(false)
  const [useLineWiseInvites, setUseLineWiseInvites] = useState(false)
  const [resolvedRequisition, setResolvedRequisition] = useState<Requisition | null>(null)
  const [lockedVendorIds, setLockedVendorIds] = useState<number[]>([])
  const [lockedLineVendorSelection, setLockedLineVendorSelection] = useState<{ [lineId: number]: number[] }>({})
  const [vendorSearchTerm, setVendorSearchTerm] = useState('')
  const [vendorCandidates, setVendorCandidates] = useState<RfqVendorCandidatesResponse>(emptyVendorCandidates)
  const [vendorCandidatesLoading, setVendorCandidatesLoading] = useState(false)
  const [vendorCandidatesError, setVendorCandidatesError] = useState('')
  const [vendorCandidatesRetryKey, setVendorCandidatesRetryKey] = useState(0)

  const suggestedVendors = vendorCandidates.suggestedVendors || []
  const otherVendors = vendorCandidates.otherVendors || []

  const getLineSelectionKey = (line: { id?: number | null }, index: number): number =>
    line.id != null ? line.id : -(index + 1)

  useEffect(() => {
    if (!visible || !rfq) return
    let active = true
      ; (async () => {
        let req =
          requisitions.find((item) => item.id === rfq.requisitionId) ||
          ((rfq as any)?.requisition as Requisition | undefined) ||
          null
        if ((req && !req.lines) || (!req && rfq.requisitionId)) {
          try {
            req = await getRequisitionById(rfq.requisitionId)
          } catch (error) {
            console.error('Failed to load requisition for RFQ edit modal', error)
          }
        }
        if (!active) return

        const invitationDetails = Array.isArray((rfq as any)?.vendorInvitationDetails)
          ? ((rfq as any).vendorInvitationDetails as Array<any>)
          : []
        const selectedVendorSet = new Set<number>()
        invitationDetails.forEach((detail) => {
          const mappingId = Number(detail?.vendorCompanyMappingId)
          if (Number.isFinite(mappingId) && mappingId > 0) {
            selectedVendorSet.add(mappingId)
            return
          }
          const vendorId = resolveInvitationVendorId(detail)
          if (vendorId != null) selectedVendorSet.add(vendorId)
        })
        const selectedVendorIds = Array.from(selectedVendorSet)
        const hasLines = !!(req?.lines && req.lines.length > 0)
        const lineVendorSelection: { [lineId: number]: number[] } = {}
        const lockedLineSelection: { [lineId: number]: number[] } = {}
        let lineWiseInvites = false

        if (hasLines) {
          req!.lines!.forEach((line, index) => {
            lineVendorSelection[getLineSelectionKey(line, index)] = []
          })
          invitationDetails.forEach((detail) => {
            const lineId = resolveInvitationLineId(detail)
            const mappingId = Number(detail?.vendorCompanyMappingId)
            const vendorId =
              Number.isFinite(mappingId) && mappingId > 0 ? mappingId : resolveInvitationVendorId(detail)
            if (lineId == null || vendorId == null) return
            lineWiseInvites = true
            const current = lineVendorSelection[lineId] || []
            if (!current.includes(vendorId)) {
              lineVendorSelection[lineId] = [...current, vendorId]
            }
            const lockedCurrent = lockedLineSelection[lineId] || []
            if (!lockedCurrent.includes(vendorId)) {
              lockedLineSelection[lineId] = [...lockedCurrent, vendorId]
            }
          })
          if (!lineWiseInvites) {
            req!.lines!.forEach((line, index) => {
              lineVendorSelection[getLineSelectionKey(line, index)] = [...selectedVendorIds]
            })
          }
        }

        setResolvedRequisition(req)
        setHasMultipleLines(hasLines)
        setUseLineWiseInvites(hasLines ? lineWiseInvites : false)
        setLockedVendorIds(selectedVendorIds)
        setLockedLineVendorSelection(lockedLineSelection)
        setErrors({})
        setVendorSearchTerm('')
        setFormData({
          requisitionId: String(rfq.requisitionId || req?.id || ''),
          requisitionLabel: req?.requisition_code || (rfq.requisitionId ? `REQ-${rfq.requisitionId}` : '-'),
          title: rfq.title || '',
          itemDescription: rfq.itemDescription || '',
          technicalSpecifications: rfq.technicalSpecifications || '',
          responseDueDate: String(rfq.responseDueDate || '').split('T')[0],
          deliveryPortId: String(
            rfq.deliveryPortOverrideId || (rfq as any)?.deliveryPortOverride?.id || ''
          ),
          selectedVendors: selectedVendorIds,
          lineVendorSelection,
        })
      })()

    return () => {
      active = false
    }
  }, [visible, rfq, requisitions])

  useEffect(() => {
    if (!visible || !formData.requisitionId) {
      return
    }
    let active = true
    const handle = window.setTimeout(async () => {
      try {
        if (active) {
          setVendorCandidatesLoading(true)
          setVendorCandidatesError('')
        }
        const response = await getRfqVendorCandidates(
          Number(formData.requisitionId),
          null,
          vendorSearchTerm
        )
        if (active) {
          const selectedMappingIds = new Set(lockedVendorIds)
          const visibleMappingIds = new Set(
            [...(response.suggestedVendors || []), ...(response.otherVendors || [])]
              .map((vendor) => resolveVendorMappingId(vendor))
              .filter((id) => id > 0)
          )
          const preservedSelectedVendors = vendors.filter((vendor) => {
            const mappingId = resolveVendorMappingId(vendor)
            return mappingId > 0 && selectedMappingIds.has(mappingId) && !visibleMappingIds.has(mappingId)
          })
          setVendorCandidates({
            ...response,
            otherVendors: [...(response.otherVendors || []), ...preservedSelectedVendors],
          })
          setVendorCandidatesLoading(false)
        }
      } catch (error) {
        console.error('Failed to load RFQ edit vendor candidates', error)
        if (active) {
          setVendorCandidatesLoading(false)
          setVendorCandidatesError('Failed to load vendor suggestions. Retry to continue.')
        }
      }
    }, 250)
    return () => {
      active = false
      window.clearTimeout(handle)
    }
  }, [visible, formData.requisitionId, vendorSearchTerm, vendorCandidatesRetryKey, lockedVendorIds])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleVendorToggle = (vendorId: number) => {
    setFormData((prev) => ({
      ...prev,
      selectedVendors:
        prev.selectedVendors.includes(vendorId) && lockedVendorIds.includes(vendorId)
          ? prev.selectedVendors
          : prev.selectedVendors.includes(vendorId)
            ? prev.selectedVendors.filter((id) => id !== vendorId)
            : [...prev.selectedVendors, vendorId],
    }))
  }

  const handleLineVendorToggle = (lineId: number, vendorId: number) => {
    setFormData((prev) => {
      const currentLineVendors = prev.lineVendorSelection[lineId] || []
      const isLockedExisting =
        currentLineVendors.includes(vendorId) &&
        (lockedLineVendorSelection[lineId] || []).includes(vendorId)
      if (isLockedExisting) {
        return prev
      }
      const newLineVendors = currentLineVendors.includes(vendorId)
        ? currentLineVendors.filter((id) => id !== vendorId)
        : [...currentLineVendors, vendorId]
      return {
        ...prev,
        lineVendorSelection: {
          ...prev.lineVendorSelection,
          [lineId]: newLineVendors,
        },
      }
    })
  }

  const isLockedVendor = (vendorId: number) => lockedVendorIds.includes(vendorId)
  const isLockedLineVendor = (lineId: number, vendorId: number) =>
    (lockedLineVendorSelection[lineId] || []).includes(vendorId)

  const validateDetails = () => {
    const newErrors: { [key: string]: string } = {}
    if (!formData.title.trim()) newErrors.title = 'RFQ Title is required'
    if (!formData.responseDueDate) {
      newErrors.responseDueDate = 'Response Due Date is required'
    } else {
      const dueDate = new Date(formData.responseDueDate)
      const dueDateOnly = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate())
      const today = new Date()
      const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate())
      if (dueDateOnly < todayOnly) {
        newErrors.responseDueDate = 'Response Due Date cannot be in the past'
      }
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const validateVendors = () => {
    const newErrors: { [key: string]: string } = {}
    if (hasMultipleLines && useLineWiseInvites) {
      const hasUnassignedLine =
        resolvedRequisition?.lines?.some((line, index) => {
          const key = getLineSelectionKey(line, index)
          return (formData.lineVendorSelection[key] || []).length === 0
        }) ?? false
      if (hasUnassignedLine) {
        newErrors.selectedVendors = 'Please select at least one vendor for each subcomponent'
      }
    } else if (formData.selectedVendors.length === 0) {
      newErrors.selectedVendors = 'Please select at least one vendor'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!rfq) return
    if (!validateDetails() || !validateVendors()) return
    setIsSubmitting(true)
    try {
      const requisition = resolvedRequisition || (rfq as any)?.requisition || null
      const requisitionCgaid = requisition?.category?.cgaid?.id ?? rfq.cgaid
      const requisitionCaid = requisition?.category?.caid?.id ?? rfq.caid ?? null
      if (!requisitionCgaid) {
        toast.error('Unable to resolve company group for this RFQ')
        return
      }

      const payload: UpdateRFQRequest = {
        requisitionId: rfq.requisitionId,
        title: formData.title,
        responseDueDate: formData.responseDueDate,
        cgaid: requisitionCgaid,
        caid: requisitionCaid,
        itemDescription: formData.itemDescription,
        technicalSpecifications: formData.technicalSpecifications,
        deliveryPortOverrideId: formData.deliveryPortId ? parseInt(formData.deliveryPortId, 10) : null,
      }

      await updateRFQ(rfq.id, payload)

      if (hasMultipleLines && resolvedRequisition?.lines?.length) {
        if (useLineWiseInvites) {
          const lineInvitations = resolvedRequisition.lines
            .filter((line) => line.id != null)
            .map((line, index) => {
              const lineKey = getLineSelectionKey(line, index)
              const vendorCompanyMappingIds = Array.from(new Set(formData.lineVendorSelection[lineKey] || []))
              return {
                requisitionLineId: line.id as number,
                vendorCompanyMappingIds,
              }
            })
            .filter((item) => item.vendorCompanyMappingIds.length > 0)
          if (lineInvitations.length > 0) {
            await inviteVendorsToRFQLines(rfq.id, lineInvitations)
          }
        } else {
          const vendorCompanyMappingIds = Array.from(new Set(formData.selectedVendors))
          if (vendorCompanyMappingIds.length > 0) {
            const lineInvitations = resolvedRequisition.lines
              .filter((line) => line.id != null)
              .map((line) => ({
                requisitionLineId: line.id as number,
                vendorCompanyMappingIds,
              }))
            if (lineInvitations.length > 0) {
              await inviteVendorsToRFQLines(rfq.id, lineInvitations)
            }
          }
        }
      } else if (formData.selectedVendors.length > 0) {
        await inviteVendorsToRFQ(rfq.id, Array.from(new Set(formData.selectedVendors)))
      }

      toast.success('RFQ updated successfully')
      onClose()
      await onSubmit()
    } catch (error: any) {
      console.error('Error updating RFQ:', error)
      toast.error('Failed to update RFQ')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!visible || !rfq) return null

  return (
    <div
      className='modal fade show d-flex align-items-center justify-content-center procurement-document-modal'
      tabIndex={-1}
      style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1050,
      }}
    >
      <div className='modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable' role='document'>
        <div className='modal-content bg-white' style={{ color: '#181C32' }}>
          <div className='modal-header'>
            <div>
              <h5 className='modal-title'>Edit RFQ</h5>
            </div>
            <button type='button' className='btn-close' onClick={onClose}></button>
          </div>

          <div className='modal-body' style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <>
              <div className='row g-3'>
                <div className='col-md-6'>
                  <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>
                    Requisition (Locked)
                  </label>
                  <input type='text' className='form-control' value={formData.requisitionLabel} readOnly />
                </div>

                <div className='col-md-12'>
                  <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>RFQ Title</label>
                  <input
                    type='text'
                    className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                    name='title'
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder='RFQ title'
                    style={{ color: '#000' }}
                  />
                  {errors.title && <div className='invalid-feedback'>{errors.title}</div>}
                </div>

                <div className='col-md-6'>
                  <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Response Due Date</label>
                  <input
                    type='date'
                    className={`form-control ${errors.responseDueDate ? 'is-invalid' : ''}`}
                    name='responseDueDate'
                    value={formData.responseDueDate}
                    min={new Date().toISOString().split('T')[0]}
                    onChange={handleInputChange}
                    style={{ color: '#000' }}
                  />
                  {errors.responseDueDate && <div className='invalid-feedback'>{errors.responseDueDate}</div>}
                </div>

                <div className='col-md-6'>
                  <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>
                    Delivery Port (Optional Override)
                  </label>
                  <SearchableSelect
                    className='form-select'
                    name='deliveryPortId'
                    value={formData.deliveryPortId}
                    onChange={handleInputChange}
                  >
                    <option value=''>Use requisition port</option>
                    {ports.map((port) => (
                      <option key={port.id} value={port.id}>
                        {port.name || port.main_port}
                        {port.code ? ` (${port.code})` : ''}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>

              </div>

              <div>
                <h6 className='fw-bold mb-3'>Edit Vendor Invitations</h6>
                <div className='d-flex flex-wrap gap-2 mb-3'>
                  {vendorCandidates.matchedRegionLabel && (
                    <span className='badge bg-success text-white'>
                      Match: {vendorCandidates.matchedRegionLabel}
                    </span>
                  )}
                  <span className={`badge ${vendorCandidates.regionMatched ? 'bg-info text-dark' : 'bg-light text-dark border'}`}>
                    {vendorCandidates.regionMatched ? 'Region matched' : 'No region filter match'}
                  </span>
                </div>
                {hasMultipleLines && (
                  <div className='alert alert-info mb-4'>
                    <div className='form-check'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        id='editLineWiseInvites'
                        checked={useLineWiseInvites}
                        onChange={(e) => setUseLineWiseInvites(e.target.checked)}
                      />
                      <label className='form-check-label' htmlFor='editLineWiseInvites'>
                        Invite vendors per subcomponent
                      </label>
                    </div>
                    <small className='text-muted d-block mt-2'>
                      Existing invitations remain intact; this adds/updates vendor invitations for this RFQ.
                    </small>
                  </div>
                )}

                {errors.selectedVendors && <div className='alert alert-danger'>{errors.selectedVendors}</div>}

                <div className='row g-3 mb-4'>
                  <div className='col-md-12'>
                    <label className='form-label fw-semibold fs-7 mb-2' style={{ color: '#181C32' }}>
                      Search vendor
                    </label>
                    <input
                      type='text'
                      className='form-control'
                      value={vendorSearchTerm}
                      onChange={(event) => setVendorSearchTerm(event.target.value)}
                      placeholder='Type vendor name, code, email, or contact'
                    />
                  </div>
                </div>

                {vendorCandidatesLoading && (
                  <div className='alert alert-secondary'>Loading vendor suggestions...</div>
                )}
                {!vendorCandidatesLoading && vendorCandidatesError && (
                  <div className='alert alert-danger d-flex justify-content-between align-items-center'>
                    <span>{vendorCandidatesError}</span>
                    <button
                      type='button'
                      className='btn btn-sm btn-light-danger'
                      onClick={() => setVendorCandidatesRetryKey((value) => value + 1)}
                    >
                      Retry
                    </button>
                  </div>
                )}

                {hasMultipleLines && useLineWiseInvites ? (
                  <div>
                    {resolvedRequisition?.lines?.map((line, lineIndex) => {
                      const lineKey = getLineSelectionKey(line, lineIndex)
                      const lineAny = line as any
                      const subcomponentLabel =
                        lineAny?.subcomponent?.name ||
                        lineAny?.subcomponentName ||
                        (line.subcomponentId ? `Subcomponent ${line.subcomponentId}` : `Subcomponent ${lineIndex + 1}`)
                      const partLabel =
                        lineAny?.part?.name ||
                        lineAny?.partName ||
                        lineAny?.manualItemName ||
                        (line.partId ? `Part ${line.partId}` : 'Item')
                      return (
                        <div key={`${line.id ?? 'line'}-${lineIndex}`} className='mb-4 border rounded p-3'>
                          <h6 className='fw-bold mb-3'>
                            {subcomponentLabel} - {partLabel}
                            <span className='badge bg-secondary ms-2'>Qty: {line.quantity}</span>
                          </h6>
                          <div className='fw-semibold mb-2'>Suggested Vendors ({suggestedVendors.length})</div>
                          <div className='row g-3 mb-3'>
                            {suggestedVendors.map((vendor) => {
                              const vendorKey = resolveVendorMappingId(vendor)
                              const isSelected = formData.lineVendorSelection[lineKey]?.includes(vendorKey) || false
                              const isLocked = isSelected && isLockedLineVendor(lineKey, vendorKey)
                              return (
                                <div key={`suggested-${vendorKey}`} className='col-md-6'>
                                  <div
                                    className={`card cursor-pointer ${isSelected ? 'border-primary' : ''}`}
                                    onClick={() => {
                                      if (!isLocked) handleLineVendorToggle(lineKey, vendorKey)
                                    }}
                                    style={{
                                      cursor: isLocked ? 'not-allowed' : 'pointer',
                                      opacity: isLocked ? 0.75 : 1,
                                      backgroundColor: isSelected ? '#F4F9FF' : 'white',
                                    }}
                                  >
                                    <div className='card-body p-2'>
                                      <div className='d-flex align-items-center'>
                                        <input
                                          type='checkbox'
                                          className='form-check-input me-2'
                                          checked={isSelected}
                                          disabled={isLocked}
                                          readOnly
                                        />
                                        <div className='flex-grow-1'>
                                          <h6 className='mb-0 fs-7 fw-bold'>{vendor.name}</h6>
                                          <small className='text-muted'>{vendor.email}</small>
                                          {vendor.matchedRegion && (
                                            <small className='d-block text-success'>{vendor.matchedRegion}</small>
                                          )}
                                          <VendorPerformanceHint vendor={vendor} category={vendorCandidates.requisitionCategory} />
                                          {isLocked && (
                                            <small className='d-block text-muted'>Already sent vendor (locked)</small>
                                          )}
                                          <VendorPerformanceHint vendor={vendor} category={vendorCandidates.requisitionCategory} />
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                          <div className='fw-semibold mb-2'>Other Vendors ({otherVendors.length})</div>
                          <div className='row g-3'>
                            {otherVendors.map((vendor) => {
                              const vendorKey = resolveVendorMappingId(vendor)
                              const isSelected = formData.lineVendorSelection[lineKey]?.includes(vendorKey) || false
                              const isLocked = isSelected && isLockedLineVendor(lineKey, vendorKey)
                              return (
                                <div key={`other-${vendorKey}`} className='col-md-6'>
                                  <div
                                    className={`card cursor-pointer ${isSelected ? 'border-primary' : ''}`}
                                    onClick={() => {
                                      if (!isLocked) handleLineVendorToggle(lineKey, vendorKey)
                                    }}
                                    style={{
                                      cursor: isLocked ? 'not-allowed' : 'pointer',
                                      opacity: isLocked ? 0.75 : 1,
                                      backgroundColor: isSelected ? '#F4F9FF' : 'white',
                                    }}
                                  >
                                    <div className='card-body p-2'>
                                      <div className='d-flex align-items-center'>
                                        <input
                                          type='checkbox'
                                          className='form-check-input me-2'
                                          checked={isSelected}
                                          disabled={isLocked}
                                          readOnly
                                        />
                                        <div className='flex-grow-1'>
                                          <h6 className='mb-0 fs-7 fw-bold'>{vendor.name}</h6>
                                          <small className='text-muted'>{vendor.email}</small>
                                          {isLocked && (
                                            <small className='d-block text-muted'>Already sent vendor (locked)</small>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div>
                    <div className='fw-semibold mb-2'>Suggested Vendors ({suggestedVendors.length})</div>
                    <div className='row g-3 mb-3'>
                      {suggestedVendors.map((vendor) => {
                        const vendorKey = resolveVendorMappingId(vendor)
                        const isSelected = formData.selectedVendors.includes(vendorKey)
                        const isLocked = isSelected && isLockedVendor(vendorKey)
                        return (
                          <div key={`suggested-${vendorKey}`} className='col-md-6'>
                            <div
                              className={`card cursor-pointer ${isSelected ? 'border-primary' : ''}`}
                              onClick={() => {
                                if (!isLocked) handleVendorToggle(vendorKey)
                              }}
                              style={{
                                cursor: isLocked ? 'not-allowed' : 'pointer',
                                opacity: isLocked ? 0.75 : 1,
                                backgroundColor: isSelected ? '#F4F9FF' : 'white',
                              }}
                            >
                              <div className='card-body p-3'>
                                <div className='d-flex align-items-center'>
                                  <input
                                    type='checkbox'
                                    className='form-check-input me-2'
                                    checked={isSelected}
                                    disabled={isLocked}
                                    readOnly
                                  />
                                  <div className='flex-grow-1'>
                                    <h6 className='mb-0 fw-bold'>{vendor.name}</h6>
                                    <p className='text-muted mb-0 fs-7'>{vendor.email}</p>
                                    {vendor.matchedRegion && (
                                      <small className='d-block text-success'>{vendor.matchedRegion}</small>
                                    )}
                                    <VendorPerformanceHint vendor={vendor} category={vendorCandidates.requisitionCategory} />
                                    {isLocked && (
                                      <small className='d-block text-muted'>Already sent vendor (locked)</small>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                    <div className='fw-semibold mb-2'>Other Vendors ({otherVendors.length})</div>
                    <div className='row g-3'>
                      {otherVendors.map((vendor) => {
                        const vendorKey = resolveVendorMappingId(vendor)
                        const isSelected = formData.selectedVendors.includes(vendorKey)
                        const isLocked = isSelected && isLockedVendor(vendorKey)
                        return (
                          <div key={`other-${vendorKey}`} className='col-md-6'>
                            <div
                              className={`card cursor-pointer ${isSelected ? 'border-primary' : ''}`}
                              onClick={() => {
                                if (!isLocked) handleVendorToggle(vendorKey)
                              }}
                              style={{
                                cursor: isLocked ? 'not-allowed' : 'pointer',
                                opacity: isLocked ? 0.75 : 1,
                                backgroundColor: isSelected ? '#F4F9FF' : 'white',
                              }}
                            >
                              <div className='card-body p-3'>
                                <div className='d-flex align-items-center'>
                                  <input
                                    type='checkbox'
                                    className='form-check-input me-2'
                                    checked={isSelected}
                                    disabled={isLocked}
                                    readOnly
                                  />
                                  <div className='flex-grow-1'>
                                    <h6 className='mb-0 fw-bold'>{vendor.name}</h6>
                                    <p className='text-muted mb-0 fs-7'>{vendor.email}</p>
                                    {isLocked && (
                                      <small className='d-block text-muted'>Already sent vendor (locked)</small>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
                {!vendorCandidatesLoading &&
                  !vendorCandidatesError &&
                  suggestedVendors.length === 0 &&
                  otherVendors.length === 0 && (
                    <p className='text-center text-muted py-5'>
                      No vendor candidates available for the current requisition and region.
                    </p>
                  )}
              </div>
            </>
          </div>

          <div className='modal-footer'>
            <button type='button' className='btn btn-light btn-sm' onClick={onClose} disabled={isSubmitting}>
              Cancel
            </button>
            <button type='button' className='btn btn_primary' onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

interface ViewRFQModalProps {
  visible: boolean
  onClose: () => void
  rfq: any | null
  onDownload?: () => void
  onQuotationChanged?: () => void | Promise<void>
  logisticsRequests?: VendorLogisticsChangeRequest[]
  canViewVendorLogistics?: boolean
  canReviewVendorLogistics?: boolean
  onOpenLogisticsRequest?: (request: VendorLogisticsChangeRequest) => void
}

const ViewRFQModal: FC<ViewRFQModalProps> = ({
  visible,
  onClose,
  rfq,
  onDownload,
  onQuotationChanged,
  logisticsRequests = [],
  canViewVendorLogistics = false,
  canReviewVendorLogistics = false,
  onOpenLogisticsRequest,
}) => {
  if (!visible || !rfq) return null

  const getStatusBadge = (status: string) => {
    const statusMap: { [key: string]: { bg: string; text: string } } = {
      DRAFT: { bg: 'bg-secondary', text: 'Draft' },
      SENT: { bg: 'bg-primary', text: 'Sent' },
      SUBMITTED: { bg: 'bg-info', text: 'Submitted' },
      PENDING_REWORK_RESPONSE: { bg: 'bg-warning', text: 'Pending Rework Response' },
      PARTIALLY_CLOSED: { bg: 'bg-warning', text: 'Partially Closed' },
      CLOSED: { bg: 'bg-success', text: 'Closed' },
      COMPLETED: { bg: 'bg-success', text: 'Completed' },
      FINALIZED: { bg: 'bg-success', text: 'Finalized' },
      CANCELLED: { bg: 'bg-danger', text: 'Cancelled' },
      RESPONDED: { bg: 'bg-success', text: 'Responded' },
      PENDING: { bg: 'bg-warning', text: 'Pending' },
    }
    const statusInfo = statusMap[status] || { bg: 'bg-secondary', text: status }
    return <span className={`badge ${statusInfo.bg} text-white`}>{statusInfo.text}</span>
  }

  return (
    <ProcurementDocumentPreviewModal
      visible={visible}
      onClose={onClose}
      title='RFQ Details'
      subtitle={rfq.title || rfq.rfqNumber || `RFQ-${rfq.id}`}
      toolbar={getStatusBadge(String(rfq.status || ''))}
      onDownload={onDownload}
    >
      <VendorQuotationEditorCard rfq={rfq} visible={visible} onChanged={onQuotationChanged} />
      <QuotationDPCDocument rfq={rfq} />
      {canViewVendorLogistics && logisticsRequests.length > 0 ? (
        <div className='mt-6 pt-6 border-top'>
          <div className='d-flex justify-content-between align-items-center gap-3 flex-wrap mb-4'>
            <div>
              <h5 className='fw-bold text-dark mb-1'>Pending Logistics Pricing Change Requests</h5>
              <p className='text-muted mb-0 fs-7'>
                Pending vendor-requested logistics pricing updates for this RFQ.
              </p>
            </div>
            <span className='badge bg-warning text-dark'>{logisticsRequests.length} Pending</span>
          </div>
          <div className='table-responsive'>
            <table className='table table-bordered align-middle'>
              <thead className='table-light'>
                <tr>
                  <th>Vendor</th>
                  <th>Quotation</th>
                  <th>PO</th>
                  <th>Requested By</th>
                  <th>Requested At</th>
                  <th className='text-center'>Action</th>
                </tr>
              </thead>
              <tbody>
                {logisticsRequests.map((request) => (
                  <tr key={request.id}>
                    <td className='fw-semibold text-dark'>{request.vendorName || request.vendorId || '-'}</td>
                    <td>{request.quotationId || '-'}</td>
                    <td>{request.poNumber || request.purchaseOrderId || '-'}</td>
                    <td>{request.requestedByUsername || '-'}</td>
                    <td>{request.requestedAt ? new Date(request.requestedAt).toLocaleString() : '-'}</td>
                    <td className='text-center'>
                      <button
                        type='button'
                        className={`btn btn-sm ${canReviewVendorLogistics ? 'btn-warning' : 'btn-light-primary'}`}
                        onClick={() => onOpenLogisticsRequest?.(request)}
                      >
                        {canReviewVendorLogistics ? 'Review Logistics Update' : 'View Logistics Update'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </ProcurementDocumentPreviewModal>
  )
}
const RFQManagement: FC = () => {
  const { currentUser } = useAuth()
  const { state: globalState, vessels: globalVessels } = useGlobalFilter()
  const [searchTerm, setSearchTerm] = useState('')
  const [referenceFilter, setReferenceFilter] = useState('')
  const [vesselFilter, setVesselFilter] = useState('')

  // Sync global header vessel filter → local vessel filter (name-based)
  useEffect(() => {
    const gv = globalState.vessel
    setVesselFilter(gv?.fleet_name || gv?.name || '')
  }, [globalState.vessel])

  const allowedVesselIds = useMemo(() => {
    if (!globalState.company) return null
    if (!globalVessels || globalVessels.length === 0) return null
    return new Set(globalVessels.map((v: any) => Number(v.id)))
  }, [globalState.company, globalVessels])

  const allowedVesselNames = useMemo(() => {
    if (!globalState.company) return null
    if (!globalVessels || globalVessels.length === 0) return null
    return new Set(
      globalVessels
        .map((v: any) => (v.fleet_name || v.name || '').trim().toLowerCase())
        .filter(Boolean)
    )
  }, [globalState.company, globalVessels])
  const [catalogueFilter, setCatalogueFilter] = useState('')
  const [requisitionTitleFilter, setRequisitionTitleFilter] = useState('')
  const [responseDueDateFromFilter, setResponseDueDateFromFilter] = useState('')
  const [responseDueDateToFilter, setResponseDueDateToFilter] = useState('')
  const [stageFilter, setStageFilter] = useState('')
  const [rfqTab, setRfqTab] = useState<'current' | 'past'>('current')
  const [additionalFiltersOpen, setAdditionalFiltersOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [rfqData, setRfqData] = useState<RFQDisplay[]>([])
  const [rfqTotalElements, setRfqTotalElements] = useState(0)
  const [rfqTotalPages, setRfqTotalPages] = useState(1)
  const [sortConfig, setSortConfig] = useState<{
    key: keyof RFQDisplay | null;
    direction: 'asc' | 'desc';
  }>({ key: null, direction: 'desc' })
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isEditModalVisible, setIsEditModalVisible] = useState(false)
  const [isViewModalVisible, setIsViewModalVisible] = useState(false)
  const [selectedRFQ, setSelectedRFQ] = useState<any | null>(null)
  const openedRfqIdRef = useRef<number | null>(null)
  const [editingRFQ, setEditingRFQ] = useState<RFQ | null>(null)
  const [rfqDownloadDoc, setRfqDownloadDoc] = useState<any | null>(null)
  const [pendingLogisticsRequests, setPendingLogisticsRequests] = useState<VendorLogisticsChangeRequest[]>([])
  const [activeLogisticsRequest, setActiveLogisticsRequest] = useState<VendorLogisticsChangeRequest | null>(null)
  const [logisticsActionLoading, setLogisticsActionLoading] = useState(false)
  const [isExportModalVisible, setIsExportModalVisible] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [requisitions, setRequisitions] = useState<Requisition[]>([])
  const [vendors, setVendors] = useState<Vendor[]>([])
  const [ports, setPorts] = useState<Port[]>([])
  const [filterVessels, setFilterVessels] = useState<Array<{ id: number; name: string }>>([])
  const [filterCatalogues, setFilterCatalogues] = useState<Array<{ id: number; name: string }>>([])
  const {
    can: canProcurementPermission,
    loading: procurementPermissionsLoading,
    getScope: getProcurementScope,
  } = useProcurementPermissions()
  const canViewRFQ = canProcurementPermission(PROCUREMENT_PERMS.RFQ_VIEW)
  const canCreateRFQ = canProcurementPermission(PROCUREMENT_PERMS.RFQ_CREATE)
  const quickCreateRequisitionId = Number(new URLSearchParams(window.location.search).get('requisitionId') || 0)
  const isQuickCreate = Number.isFinite(quickCreateRequisitionId) && quickCreateRequisitionId > 0
  const canEditRFQPermission = canProcurementPermission(PROCUREMENT_PERMS.RFQ_EDIT)
  const canSendRFQPermission = canProcurementPermission(PROCUREMENT_PERMS.RFQ_SEND)
  const canDeleteRFQPermission = canProcurementPermission(PROCUREMENT_PERMS.RFQ_EDIT)
  const canDownloadRFQ =
    canProcurementPermission(PROCUREMENT_PERMS.RFQ_DOWNLOAD) ||
    canProcurementPermission(PROCUREMENT_PERMS.RFQ_VIEW)
  const canExportRFQ =
    canProcurementPermission(PROCUREMENT_PERMS.RFQ_EXPORT) || canDownloadRFQ
  const canViewVendorLogisticsRequests =
    canProcurementPermission(PROCUREMENT_PERMS.PO_VENDOR_LOGISTICS_VIEW) ||
    canProcurementPermission(PROCUREMENT_PERMS.PO_VENDOR_LOGISTICS_REVIEW)
  const canReviewVendorLogisticsRequests = canProcurementPermission(
    PROCUREMENT_PERMS.PO_VENDOR_LOGISTICS_REVIEW
  )

  const rfqDataScope =
    getProcurementScope(PROCUREMENT_PERMS.RFQ_VIEW) ??
    getProcurementScope(PROCUREMENT_PERMS.READ_REQUISITION) ??
    'VESSEL'

  // Get vesselId from user context
  const vesselId = currentUser?.vessel?.id ?? null
  const currentCompanyGroupAdminId = currentUser?.companyGroupAdminId ?? null

  const loadPendingLogisticsRequests = useCallback(async () => {
    if (!canViewVendorLogisticsRequests) {
      setPendingLogisticsRequests([])
      return
    }
    try {
      const rows = await listVendorLogisticsChangeRequests({ status: 'PENDING' })
      setPendingLogisticsRequests(rows)
    } catch (error) {
      console.error('Error fetching vendor logistics change requests:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to load vendor logistics requests')
    }
  }, [canViewVendorLogisticsRequests])

  const clearQuickCreateQuery = () => {
    const url = new URL(window.location.href)
    url.searchParams.delete('requisitionId')
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`)
  }

  useEffect(() => {
    void loadPendingLogisticsRequests()
  }, [loadPendingLogisticsRequests])

  const pendingLogisticsRequestsByRfqId = useMemo(
    () => groupVendorLogisticsRequestsByRfqId(pendingLogisticsRequests),
    [pendingLogisticsRequests]
  )

  useEffect(() => {
    loadInitialData()

    // Check if there's a requisitionId in URL parameters
    const urlParams = new URLSearchParams(window.location.search)
    const requisitionId = urlParams.get('requisitionId')
    if (requisitionId && canCreateRFQ) {
      // Open modal automatically if requisitionId is provided
      setIsModalVisible(true)
    }
  }, [vesselId, rfqDataScope, canCreateRFQ])

  useEffect(() => {
    // Load RFQs when component mounts or when ports/requisitions are loaded
    loadRFQs()
  }, [
    ports,
    requisitions,
    currentCompanyGroupAdminId,
    rfqDataScope,
    vesselId,
    currentPage,
    rowsPerPage,
    searchTerm,
    referenceFilter,
    vesselFilter,
    catalogueFilter,
    requisitionTitleFilter,
    responseDueDateFromFilter,
    responseDueDateToFilter,
    stageFilter,
    rfqTab,
    sortConfig,
  ])

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const rfqId = Number(urlParams.get('rfqId') || 0)
    if (!Number.isFinite(rfqId) || rfqId <= 0 || openedRfqIdRef.current === rfqId) {
      return
    }
    openedRfqIdRef.current = rfqId
    void handleViewRFQ({id: rfqId} as RFQDisplay)
  }, [currentPage, rowsPerPage, rfqData.length])

  useEffect(() => {
    setCurrentPage(1)
  }, [rfqTab])

  useEffect(() => {
    setCurrentPage(1)
  }, [
    searchTerm,
    referenceFilter,
    vesselFilter,
    catalogueFilter,
    requisitionTitleFilter,
    responseDueDateFromFilter,
    responseDueDateToFilter,
    stageFilter,
    rowsPerPage,
  ])

  const loadInitialData = async () => {
    try {
      const requisitionVesselFilter = rfqDataScope === 'ALL' ? null : vesselId
      const [reqResponse, vendorResponse, portsData, vendorCategoriesData, vesselsData, categoriesData] = await Promise.all([
        getRequisitions(0, 200, {
          vesselId: requisitionVesselFilter,
          status: 'SHORE_APPROVED',
        }),
        getVendors(0, 1000),
        getPorts(),
        getVendorCategories(),
        getAllVessels(),
        getInventoryCategoriesForResolvedTenant(currentCompanyGroupAdminId ?? undefined),
      ])
      console.log('RFQ - All requisitions:', reqResponse.content.map(r => ({ id: r.id, code: r.requisition_code, status: r.status })))
      console.log('RFQ - Shore approved requisitions:', reqResponse.content.filter(r => r.status === 'SHORE_APPROVED'))
      console.log('RFQ - Unique status values:', Array.from(new Set(reqResponse.content.map(r => r.status))))
      console.log('RFQ - Vendors loaded:', vendorResponse.content)
      console.log('RFQ - Vendor categories loaded:', vendorCategoriesData)

      // Map vendors to include category names instead of just IDs
      const vendorsWithCategories = vendorResponse.content.map(vendor => ({
        ...vendor,
        categories: vendor.categoryIds
          ? vendor.categoryIds.map(catId => {
            const category = vendorCategoriesData.find(c => c.id === catId)
            return category ? category.name : `Category ${catId}`
          })
          : []
      }))

      console.log('RFQ - Vendors with categories:', vendorsWithCategories)

      setRequisitions(reqResponse.content)
      setVendors(vendorsWithCategories)
      setPorts(portsData)
      setFilterVessels(
        (vesselsData || [])
          .map((vessel) => ({
            id: Number(vessel.id),
            name: pickFirstText((vessel as any).fleet_name, (vessel as any).fleetName, vessel.name) || `Vessel ${vessel.id}`,
          }))
          .filter((vessel) => Number.isFinite(vessel.id) && vessel.id > 0)
      )
      setFilterCatalogues(
        (categoriesData || [])
          .map((category) => ({ id: Number(category.id), name: String(category.name || '').trim() }))
          .filter((category) => Number.isFinite(category.id) && category.id > 0 && category.name)
      )
    } catch (error) {
      console.error('Error loading initial data:', error)
    }
  }

  const loadRFQs = async () => {
    setIsLoading(true)
    try {
      await loadPendingLogisticsRequests()
      // Get cgaid from current user for filtering RFQs (optional - load all if not set)
      const cgaid = currentCompanyGroupAdminId
      const scopedVesselId = rfqDataScope === 'ALL' ? null : vesselId
      const selectedVesselId =
        vesselFilter.trim() !== ''
          ? Number(vesselFilter)
          : scopedVesselId
      const serverSearchQuery = searchTerm.trim()
      const serverStatusFilter = stageFilter.trim() || undefined
      const serverSort =
        sortConfig.key === null
          ? ['updatedAt,desc', 'id,desc']
          : [
              `${
                sortConfig.key === 'rfqNumber'
                  ? 'rfqNumber'
                    : sortConfig.key === 'responseDueDate'
                      ? 'responseDueDate'
                      : 'id'
              },${sortConfig.direction}`,
            ]
      console.log('Loading RFQs with cgaid:', cgaid)
      const rfqPage = await getRFQsPage({
        cgaid: cgaid ?? undefined,
        status: serverStatusFilter,
        vesselId: Number.isFinite(selectedVesselId) && Number(selectedVesselId) > 0 ? Number(selectedVesselId) : undefined,
        q: serverSearchQuery || undefined,
        reference: referenceFilter.trim() || undefined,
        categoryId: catalogueFilter.trim() ? Number(catalogueFilter) : undefined,
        requisitionTitle: requisitionTitleFilter.trim() || undefined,
        tab: rfqTab,
        responseDueDateFrom: responseDueDateFromFilter || undefined,
        responseDueDateTo: responseDueDateToFilter || undefined,
        page: currentPage - 1,
        size: rowsPerPage,
        sort: serverSort,
      })
      const rfqs = rfqPage.content || []
      setRfqTotalElements(Number(rfqPage.totalElements ?? rfqs.length ?? 0))
      setRfqTotalPages(Math.max(1, Number(rfqPage.totalPages ?? 1)))
      console.log('RFQ Response:', rfqs)
      console.log('RFQs loaded:', rfqs.length)

      // Process RFQs for display
      const displayItemsPromises = rfqs.map(async (rfq: RFQListSummary) => {
        const req = requisitions.find(r => r.id === rfq.requisitionId)
        const requestFor = rfq.requestFor || req?.requestFor || null
        const vesselName = pickFirstText(rfq.vesselName, req?.vesselName) || resolveVesselName(req, rfq.vesselId)
        const department = pickFirstText(rfq.categoryName) || 'N/A'
        const catalogue = pickFirstText(rfq.categoryName, req?.category?.name) || 'N/A'
        const requisitionTitle = pickFirstText(rfq.requisitionTitle, req?.name, req?.title)
        const itemName =
          pickFirstText(rfq.itemDescription, requisitionTitle, rfq.title) || `RFQ-${rfq.id}`
        const vendorCount = Math.max(0, Number(rfq.invitedVendorCount ?? 0))
        const responseCount = Math.max(0, Number(rfq.responseCount ?? 0))

        return {
          id: rfq.id,
          rfqNumber: rfq.title || rfq.rfqNumber || requisitionTitle || `RFQ ${rfq.id}`,
          status: rfq.status,
          itemName,
          itemDescription: rfq.itemDescription || '',
          requisitionNumber: rfq.requisitionCode || req?.requisition_code,
          responseDueDate: rfq.responseDueDate,
          deliveryPort: rfq.deliveryPortName || 'N/A',
          estimatedValue: 'N/A',
          quantity: 'N/A',
          requestFor,
          vendors: vendorCount,
          responses: `${responseCount}/${vendorCount || Math.max(responseCount, 1)}`,
          logisticsTotal: Number(rfq.logisticsTotal ?? 0),
          quotationCurrency: rfq.quotationCurrency || null,
          vendorDetails: [],
          vesselId: rfq.vesselId ?? req?.vesselId ?? null,
          vesselName,
          department,
          catalogue,
          requisitionTitle,
        }
      })

      const displayItems = await Promise.all(displayItemsPromises)
      setRfqData(displayItems)
    } catch (error: any) {
      console.error('Error loading RFQs:', error)
      toast.error('Failed to load RFQs')
      setRfqData([])
      setRfqTotalElements(0)
      setRfqTotalPages(1)
    } finally {
      setIsLoading(false)
    }
  }

  const handleAddRFQSuccess = async () => {
    // Reload RFQs from API to get the latest data
    console.log('RFQ created successfully, reloading RFQ list...')
    await loadRFQs()
    // Refresh the server-filtered requisition options so the source requisition
    // disappears immediately after transitioning to RFQ_ISSUED.
    await loadInitialData()
    console.log('RFQ list reloaded')
  }

  const handleDeleteRFQ = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this RFQ?')) return

    try {
      await deleteRFQ(id)
      toast.success('RFQ deleted successfully!')
      await loadRFQs()
    } catch (error: any) {
      console.error('Error deleting RFQ:', error)
      toast.error('Failed to delete RFQ')
    }
  }

  const handleEditRFQ = async (record: RFQDisplay) => {
    try {
      const fullRFQ = await getRFQById(record.id)
      let requisition =
        requisitions.find((req) => req.id === fullRFQ.requisitionId) ||
        ((fullRFQ as any)?.requisition as Requisition | undefined) ||
        null
      if (!requisition && fullRFQ.requisitionId) {
        requisition = await getRequisitionById(fullRFQ.requisitionId)
      }
      setEditingRFQ({
        ...fullRFQ,
        requisition: requisition || (fullRFQ as any)?.requisition || null,
      } as RFQ)
      setIsEditModalVisible(true)
    } catch (error: any) {
      console.error('Error loading RFQ for edit:', error)
      toast.error('Failed to load RFQ for edit')
    }
  }

  const loadRFQDocumentPayload = async (recordOrId: RFQDisplay | number) => {
    const rfqId = typeof recordOrId === 'number' ? recordOrId : recordOrId.id
    const fallbackRecord =
      typeof recordOrId === 'number'
        ? rfqData.find((rfq) => rfq.id === rfqId)
        : recordOrId
    const fullRFQ = await getRFQById(rfqId)
    const reqDetail = await getRequisitionById(fullRFQ.requisitionId)
    const quotationsPage = await getQuotationsByRFQPage(rfqId, 0, 200)
    const quotations = (quotationsPage.content || []).map((quotation: any) => {
      const fallbackVendor = vendors.find((vendor) => vendor.id === quotation?.vendorId)
      return {
        ...quotation,
        vendor:
          quotation?.vendor ||
          (fallbackVendor
            ? { id: fallbackVendor.id, name: fallbackVendor.name, code: fallbackVendor.code }
            : null),
        lines: Array.isArray(quotation?.lines) ? quotation.lines : [],
        attachments: Array.isArray(quotation?.attachments) ? quotation.attachments : [],
      }
    })
    const selectedQuotations = quotations.filter(
      (quotation: any) => String(quotation?.status || '').toUpperCase() === 'SELECTED'
    )
    const invitationDetailsRaw = Array.isArray((fullRFQ as any)?.vendorInvitationDetails)
      ? (((fullRFQ as any).vendorInvitationDetails as Array<any>) || [])
      : []
    const normalizedInvitationDetails = invitationDetailsRaw.map((detail) => {
      const vendorId = resolveInvitationVendorId(detail)
      const fallbackVendor = vendorId != null ? vendors.find((vendor) => vendor.id === vendorId) : null
      return {
        ...detail,
        vendorId: vendorId ?? detail?.vendorId,
        vendorName: resolveInvitationVendorName(detail) || fallbackVendor?.name || '-',
        vendorEmail: String(detail?.vendorEmail || detail?.vendor?.email || fallbackVendor?.email || ''),
        invitationStatus: resolveInvitationStatus(detail) || String(detail?.invitationStatus || detail?.status || ''),
        requisitionLineId: resolveInvitationLineId(detail) ?? detail?.requisitionLineId,
      }
    })
    const fallbackInvitationDetails =
      normalizedInvitationDetails.length > 0
        ? normalizedInvitationDetails
        : (fullRFQ.invitedVendorIds || []).map((vendorId) => {
          const vendor = vendors.find((item) => item.id === vendorId)
          return {
            vendorId,
            vendorName: vendor?.name || `Vendor ${vendorId}`,
            vendorEmail: vendor?.email || '',
            invitationStatus: 'INVITED',
          }
        })

    return {
      ...fullRFQ,
      title: fullRFQ.title || fallbackRecord?.rfqNumber || `RFQ-${fullRFQ.id}`,
      status: fullRFQ.status || fallbackRecord?.status,
      requisition: reqDetail || (fullRFQ as any).requisition || null,
      quotations,
      selectedQuotations,
      vendorInvitationDetails: fallbackInvitationDetails,
      attachments: Array.isArray(fullRFQ.attachments) ? fullRFQ.attachments : [],
    }
  }

  const handleViewRFQ = async (record: RFQDisplay) => {
    try {
      const modalRecord = await loadRFQDocumentPayload(record)
      setSelectedRFQ(modalRecord)
      setIsViewModalVisible(true)
    } catch (error) {
      console.error('Error fetching RFQ details:', error)
      toast.error('Failed to load RFQ details')
    }
  }

  useEffect(() => {
    if (!isViewModalVisible && openedRfqIdRef.current != null) {
      const params = new URLSearchParams(window.location.search)
      if (params.get('rfqId')) {
        params.delete('rfqId')
        const query = params.toString()
        window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`)
      }
      openedRfqIdRef.current = null
    }
  }, [isViewModalVisible])

  const refreshSelectedRfqView = async () => {
    if (!selectedRFQ?.id) {
      await loadRFQs()
      return
    }
    const refreshed = await loadRFQDocumentPayload(Number(selectedRFQ.id))
    setSelectedRFQ(refreshed)
    await loadRFQs()
  }

  const refreshAfterLogisticsReview = async () => {
    await Promise.all([loadPendingLogisticsRequests(), loadRFQs()])
  }

  const handleApproveLogisticsRequest = async (requestId: number) => {
    try {
      setLogisticsActionLoading(true)
      await approveVendorLogisticsChangeRequest(requestId)
      toast.success('Logistics pricing change approved')
      setActiveLogisticsRequest(null)
      await refreshAfterLogisticsReview()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to approve logistics request')
    } finally {
      setLogisticsActionLoading(false)
    }
  }

  const handleRejectLogisticsRequest = async (requestId: number, reason: string) => {
    try {
      setLogisticsActionLoading(true)
      await rejectVendorLogisticsChangeRequest(requestId, reason)
      toast.success('Logistics pricing change rejected')
      setActiveLogisticsRequest(null)
      await refreshAfterLogisticsReview()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to reject logistics request')
    } finally {
      setLogisticsActionLoading(false)
    }
  }

  const openLogisticsAction = async (record: RFQDisplay) => {
    const requests = pendingLogisticsRequestsByRfqId.get(Number(record.id)) || []
    if (requests.length === 0) return
    if (requests.length === 1) {
      setActiveLogisticsRequest(requests[0])
      return
    }
    await handleViewRFQ(record)
  }

  const handleDownloadRFQ = async (
    recordOrId: RFQDisplay | number,
    options?: { silent?: boolean }
  ) => {
    if (!canDownloadRFQ) {
      if (!options?.silent) {
        toast.error('You do not have RFQ download access')
      }
      return
    }
    const rfqId = typeof recordOrId === 'number' ? recordOrId : recordOrId.id
    try {
      const payload = await loadRFQDocumentPayload(recordOrId)
      setRfqDownloadDoc(payload)
      await new Promise((resolve) => window.setTimeout(resolve, 160))
      await downloadElementAsPdf(
        '#rfq-download-host',
        `RFQ_${payload?.title || payload?.rfqNumber || rfqId}`
      )
      if (!options?.silent) {
        toast.success('RFQ PDF downloaded')
      }
    } catch (error) {
      console.error('Failed to download RFQ PDF', error)
      if (!options?.silent) {
        toast.error('Failed to download RFQ PDF')
      }
      throw error
    } finally {
      setRfqDownloadDoc(null)
    }
  }

  const handleSendRFQ = async (id: number) => {
    if (!window.confirm('Are you sure you want to send this RFQ to vendors?')) return

    try {
      await sendRFQ(id)
      toast.success('RFQ sent successfully!')
      await loadRFQs()
    } catch (error: any) {
      console.error('Error sending RFQ:', error)
      toast.error(error?.message || 'Failed to send RFQ')
    }
  }

  const stageOptions: FilterOption[] = [
    { value: '', label: 'All Stages' },
    { value: 'DRAFT', label: 'Draft' },
    { value: 'SENT', label: 'Sent' },
    { value: 'PENDING_REWORK_RESPONSE', label: 'Pending Rework Response' },
    { value: 'PARTIALLY_CLOSED', label: 'Partially Closed' },
    { value: 'CLOSED', label: 'Closed' },
    { value: 'CANCELLED', label: 'Cancelled' },
  ]

  const vesselOptions = useMemo<FilterOption[]>(() => {
    const sorted = [...filterVessels].sort((a, b) => a.name.localeCompare(b.name))
    return [{ value: '', label: 'All Vessels' }, ...sorted.map((vessel) => ({ value: String(vessel.id), label: vessel.name }))]
  }, [filterVessels])


  const catalogueOptions = useMemo<FilterOption[]>(() => {
    const values = Array.from(new Set(rfqData.map((r) => r.catalogue).filter(Boolean))).sort()
    return [{ value: '', label: 'All Catalogues' }, ...values.map((v) => ({ value: String(v), label: String(v) }))]
  }, [rfqData])

  const isPastRFQStatus = useCallback((status: string) => {
    const normalized = String(status || '').toUpperCase()
    return (
      normalized === 'CLOSED' ||
      normalized === 'COMPLETED' ||
      normalized === 'FINALIZED' ||
      normalized === 'CANCELLED'
    )
  }, [])

  const filteredData = useMemo(() => {
    return rfqData.filter(record => {
      const matchesSearch = searchTerm === '' ||
        record.rfqNumber.toLowerCase().includes(searchTerm.toLowerCase())

      const matchesReference =
        referenceFilter === '' ||
        record.rfqNumber.toLowerCase().includes(referenceFilter.toLowerCase()) ||
        (record.requisitionNumber || '').toLowerCase().includes(referenceFilter.toLowerCase())

      // 1. Company Filter Check (when company is selected in global header)
      let matchesCompany = true
      if (allowedVesselIds || allowedVesselNames) {
        const vId = Number(record.vesselId)
        const vName = (record.vesselName || '').trim().toLowerCase()
        const matchesId = vId > 0 && allowedVesselIds?.has(vId)
        const matchesName = vName && allowedVesselNames && Array.from(allowedVesselNames).some(an => vName.includes(an) || an.includes(vName))
        matchesCompany = Boolean(matchesId || matchesName)
      }

      // 2. Vessel Filter Check (when vessel is selected in global header)
      let matchesVessel = true
      if (globalState.vessel) {
        const selVesselId = Number(globalState.vessel.id)
        const selVesselName = (globalState.vessel.fleet_name || globalState.vessel.name || '').trim().toLowerCase()
        const recordVesselId = Number(record.vesselId)
        const recordVesselName = (record.vesselName || '').trim().toLowerCase()

        if (selVesselId > 0 && recordVesselId > 0) {
          matchesVessel = recordVesselId === selVesselId
        } else if (selVesselName && recordVesselName) {
          matchesVessel = recordVesselName.includes(selVesselName) || selVesselName.includes(recordVesselName)
        } else {
          matchesVessel = vesselFilter === '' || recordVesselName.includes(vesselFilter.toLowerCase())
        }
      } else if (vesselFilter) {
        const recordVesselName = (record.vesselName || '').trim().toLowerCase()
        matchesVessel = recordVesselName.includes(vesselFilter.toLowerCase())
      }


      const matchesCatalogue =
        catalogueFilter === '' ||
        (record.catalogue || '').toLowerCase().includes(catalogueFilter.toLowerCase())

      const matchesReqTitle =
        requisitionTitleFilter === '' ||
        (record.requisitionTitle || '').toLowerCase().includes(requisitionTitleFilter.toLowerCase())

      const matchesDateFrom = responseDueDateFromFilter === '' || (record.responseDueDate || '') >= responseDueDateFromFilter
      const matchesDateTo = responseDueDateToFilter === '' || (record.responseDueDate || '') <= responseDueDateToFilter

      const matchesStage = stageFilter === '' || record.status === stageFilter

      const matchesTab =
        rfqTab === 'past'
          ? isPastRFQStatus(record.status)
          : !isPastRFQStatus(record.status)

      return (
        matchesSearch &&
        matchesReference &&
        matchesCompany &&
        matchesVessel &&
        matchesCatalogue &&
        matchesReqTitle &&
        matchesDateFrom &&
        matchesDateTo &&
        matchesStage &&
        matchesTab
      )
    })
  }, [
    searchTerm,
    rfqData,
    referenceFilter,
    vesselFilter,
    globalState.vessel,
    globalState.company,
    allowedVesselIds,
    allowedVesselNames,
    catalogueFilter,
    requisitionTitleFilter,
    responseDueDateFromFilter,
    responseDueDateToFilter,
    stageFilter,
    rfqTab,
    isPastRFQStatus,
  ])

  const sortedData = useMemo(() => {
    let sortedRecords = [...filteredData]

    if (sortConfig.key !== null) {
      sortedRecords.sort((a, b) => {
        const aVal = String(a[sortConfig.key!] || '').toLowerCase()
        const bVal = String(b[sortConfig.key!] || '').toLowerCase()

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1
        return 0
      })
    }
    return sortedRecords
  }, [filteredData, sortConfig])

  const handleSort = (key: keyof RFQDisplay) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  const filteredTotalElements = rfqTotalElements
  const currentRecords = rfqData
  const totalPages = Math.max(1, rfqTotalPages)

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10))
    setCurrentPage(1)
  }

  const resetFilters = () => {
    setSearchTerm('')
    setReferenceFilter('')
    setVesselFilter('')
    setCatalogueFilter('')
    setRequisitionTitleFilter('')
    setResponseDueDateFromFilter('')
    setResponseDueDateToFilter('')
    setStageFilter('')
    setCurrentPage(1)
  }

  const exportRFQsToPDF = () => {
    if (!canExportRFQ) {
      toast.error('You do not have RFQ export access')
      return
    }
    setIsExportModalVisible(true)
  }

  const exportRecordOptions = useMemo<ProcurementExportRecord[]>(
    () =>
      rfqData.map((record) => ({
        id: record.id,
        label: `${record.rfqNumber || `RFQ-${record.id}`} - ${record.itemName || 'RFQ'}`,
        date: record.responseDueDate || '',
        vessel: record.vesselName || '',
        status: record.status || '',
      })),
    [rfqData]
  )

  const handleRFQExportSubmit = async (selection: ProcurementExportSelection) => {
    if (!selection.matchedRecordIds.length) {
      toast.warning('No matching RFQs found for export')
      return
    }
    setIsExporting(true)
    try {
      for (const rfqId of selection.matchedRecordIds) {
        await handleDownloadRFQ(rfqId, { silent: true })
      }
      toast.success(
        selection.matchedRecordIds.length === 1
          ? 'RFQ export ready'
          : `${selection.matchedRecordIds.length} RFQs exported`
      )
      setIsExportModalVisible(false)
    } catch (error) {
      console.error('RFQ export failed', error)
      toast.error('Failed to export RFQs')
    } finally {
      setIsExporting(false)
    }
  }

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      'DRAFT': 'bg-secondary',
      'SENT': 'bg-primary',
      'SUBMITTED': 'bg-info',
      'RESPONDED': 'bg-success',
      'PENDING': 'bg-warning',
      'PENDING_REWORK_RESPONSE': 'bg-warning',
      'PARTIALLY_CLOSED': 'bg-warning',
      'CLOSED': 'bg-secondary',
      'COMPLETED': 'bg-success',
      'FINALIZED': 'bg-success',
      'CANCELLED': 'bg-danger',
    }
    return (
      <span className={`badge ${statusColors[status] || 'bg-secondary'} text-white fw-bold`}>
        {status}
      </span>
    )
  }

  if (procurementPermissionsLoading) {
    return (
      <div className='d-flex align-items-center justify-content-center py-10'>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading procurement permissions...</span>
        </div>
      </div>
    )
  }

  if (!canViewRFQ) {
    return <div className='alert alert-warning m-6'>You do not have RFQ view access.</div>
  }

  return (
    <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
      <div className='d-flex flex-column flex-column-fluid'>
        <div id='kt_app_content' className='app-content flex-column-fluid bg-white'>
          <div className='card'>
            <div className='card-header border-0 pt-6 d-flex justify-content-between align-items-center bg-white'>
              <div>
                <h3 className='card-label text-dark fw-bold'>RFQ Management</h3>
                <p className='text-muted mb-0'>Create and manage requests for quotations</p>
              </div>
              <div className='card-toolbar d-flex gap-2'>
                {canDownloadRFQ && (
                  <button
                    type='button'
                    className='btn btn_primary'
                    style={{
                      backgroundColor: '',
                      color: 'white',
                      border: 'none'
                    }}
                    onClick={exportRFQsToPDF}
                  >
                    <KTSVG path='/media/icons/duotune/arrows/arr078.svg' className='svg-icon-2' />
                    Export
                  </button>
                )}
                {canCreateRFQ && (
                  <button
                    type='button'
                    className='btn btn_primary'
                    onClick={() => setIsModalVisible(true)}
                  >
                    <span className='me-2'>+</span>
                    Create RFQ
                  </button>
                )}
              </div>
            </div>

            <div className='card-body py-4 bg-white border-top'>
              <ProcurementFilterPanel
                searchValue={searchTerm}
                onSearchChange={(value) => {
                  setSearchTerm(value)
                  setCurrentPage(1)
                }}
                searchPlaceholder='Search RFQs...'
                referenceValue={referenceFilter}
                onReferenceChange={(value) => {
                  setReferenceFilter(value)
                  setCurrentPage(1)
                }}

                catalogueValue={catalogueFilter}
                onCatalogueChange={(value) => {
                  setCatalogueFilter(value)
                  setCurrentPage(1)
                }}
                catalogueOptions={catalogueOptions}
                requisitionTitleValue={requisitionTitleFilter}
                onRequisitionTitleChange={(value) => {
                  setRequisitionTitleFilter(value)
                  setCurrentPage(1)
                }}
                poDateFrom={responseDueDateFromFilter}
                onPoDateFromChange={(value) => {
                  setResponseDueDateFromFilter(value)
                  setCurrentPage(1)
                }}
                poDateTo={responseDueDateToFilter}
                onPoDateToChange={(value) => {
                  setResponseDueDateToFilter(value)
                  setCurrentPage(1)
                }}
                dateFromLabel='Response Due Date From'
                dateToLabel='Response Due Date To'
                stageValue={stageFilter}
                onStageChange={(value) => {
                  setStageFilter(value)
                  setCurrentPage(1)
                }}
                stageOptions={stageOptions}
                additionalOpen={additionalFiltersOpen}
                onToggleAdditional={() => setAdditionalFiltersOpen((prev) => !prev)}
                onReset={resetFilters}
              />

              <div className='mb-8'>
                <div className='d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4'>
                  <div className='d-flex gap-2'>
                    <button
                      type='button'
                      className={`btn btn-sm ${rfqTab === 'current' ? 'btn-primary' : 'btn-light-primary'}`}
                      onClick={() => setRfqTab('current')}
                    >
                      Current RFQs
                    </button>
                    <button
                      type='button'
                      className={`btn btn-sm ${rfqTab === 'past' ? 'btn-primary' : 'btn-light-primary'}`}
                      onClick={() => setRfqTab('past')}
                    >
                      Past RFQs
                    </button>
                  </div>
                  <h4 className='fw-bold text-primary mb-0'>
                    {rfqTab === 'past' ? 'Past RFQs' : 'Current RFQs'} ({currentRecords.length})
                  </h4>
                </div>

                {isLoading ? (
                  <div className='text-center py-5'>
                    <div className='spinner-border text-primary' role='status'>
                      <span className='visually-hidden'>Loading...</span>
                    </div>
                  </div>
                ) : (
                  <div className='report-table table-responsive'>
                    <div style={{ overflowX: 'auto' }}>
                      <table className='table table-bordered align-middle procurement-list-table'>
                        <thead className='table-header text-start'>
                          <tr>
                            <th onClick={() => handleSort('rfqNumber')} className='cursor-pointer align-middle' style={{ padding: '12px 16px' }}>
                              <div className='d-flex align-items-center'>
                                <span style={{ color: '#3F4254', fontWeight: 600 }}>RFQ TITLE</span>
                                <div style={{ transform: 'translateY(-2px)' }}>
                                  <KTSVG
                                    path={`/media/map/sort-col-${sortConfig.key === 'rfqNumber' ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                    className='svg-icon ms-2 custom-sort-icon'
                                  />
                                </div>
                              </div>
                            </th>
                            <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>STATUS</th>
                            <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>REQUISITION</th>
                            <th onClick={() => handleSort('responseDueDate')} className='cursor-pointer align-middle' style={{ padding: '12px 16px' }}>
                              <div className='d-flex align-items-center'>
                                <span style={{ color: '#3F4254', fontWeight: 600 }}>DUE DATE</span>
                                <div style={{ transform: 'translateY(-2px)' }}>
                                  <KTSVG
                                    path={`/media/map/sort-col-${sortConfig.key === 'responseDueDate' ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                    className='svg-icon ms-2 custom-sort-icon'
                                  />
                                </div>
                              </div>
                            </th>
                            <th className='align-middle text-center' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>VENDORS</th>
                            <th className='align-middle text-center' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>RESPONSES</th>
                            <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>LOGISTICS TOTAL</th>
                            <th className='align-middle text-center procurement-actions-cell' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>ACTIONS</th>
                          </tr>
                        </thead>
                        <tbody className='table-body text-start'>
                          {currentRecords.length === 0 ? (
                            <tr>
                              <td colSpan={8} className='text-center text-muted py-5'>
                                No {rfqTab === 'past' ? 'past' : 'current'} RFQs found for the selected criteria.
                              </td>
                            </tr>
                          ) : (
                            currentRecords.map((record) => (
                              <tr key={record.id} style={{ borderBottom: '1px solid #E4E6EF' }}>
                                <td className='text-dark fw-semibold fs-6' style={{ padding: '12px 16px' }}>
                                  <div className='d-flex align-items-center gap-2 flex-wrap'>
                                    <span>{record.rfqNumber}</span>
                                    {canViewVendorLogisticsRequests &&
                                      (pendingLogisticsRequestsByRfqId.get(Number(record.id))?.length || 0) > 0 ? (
                                      <span className='badge bg-warning text-dark'>Logistics Update Pending</span>
                                    ) : null}
                                  </div>
                                </td>
                                <td style={{ padding: '12px 16px' }}>{getStatusBadge(record.status)}</td>
                                <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>
                                  {record.requisitionNumber ? (
                                    <a href='#' className='text-primary text-hover-primary'>{record.requisitionNumber}</a>
                                  ) : (
                                    <span className='text-muted'>Manual</span>
                                  )}
                                </td>
                                <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>{record.responseDueDate}</td>
                                <td className='text-dark fs-6 text-center' style={{ padding: '12px 16px' }}>{record.vendors}</td>
                                <td className='text-dark fs-6 text-center' style={{ padding: '12px 16px' }}>{record.responses}</td>
                                <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>
                                  {(() => {
                                    const originalCurrency = String(record.quotationCurrency || '').trim().toUpperCase() || null
                                    const companyCurrency =
                                      String(record.companyCurrencyCode || '').trim().toUpperCase() || null
                                    const originalAmount =
                                      record.logisticsTotal != null ? Number(record.logisticsTotal) : null
                                    const companyAmount =
                                      record.companyLogisticsTotal != null
                                        ? Number(record.companyLogisticsTotal)
                                        : null
                                    const sameCurrency =
                                      companyCurrency != null &&
                                      originalCurrency != null &&
                                      companyCurrency === originalCurrency

                                    return (
                                      <MoneyDisplay
                                        compact
                                        primaryAmount={companyAmount ?? originalAmount}
                                        primaryCurrency={companyAmount != null ? companyCurrency : originalCurrency}
                                        secondaryAmount={
                                          companyAmount != null && !sameCurrency ? originalAmount : null
                                        }
                                        secondaryCurrency={
                                          companyAmount != null && !sameCurrency ? originalCurrency : null
                                        }
                                        sameCurrency={sameCurrency}
                                        exchangeRateDisplay={
                                          companyAmount != null &&
                                          !sameCurrency &&
                                          originalCurrency &&
                                          companyCurrency
                                            ? formatExchangeRateDisplay(
                                                originalCurrency,
                                                record.exchangeRate,
                                                companyCurrency
                                              )
                                            : null
                                        }
                                      />
                                    )
                                  })()}
                                </td>
                                <td className='text-center procurement-actions-cell' style={{ padding: '12px 16px' }}>
                                  <ProcurementActionGrid>
                                    {canSendRFQPermission &&
                                      String(record.status || '').toUpperCase() === 'DRAFT' && (
                                        <button
                                          className='btn btn-sm btn-primary'
                                          title='Send RFQ'
                                          onClick={() => handleSendRFQ(record.id)}
                                        >
                                          Send
                                        </button>
                                      )}
                                    {canEditRFQPermission &&
                                      ['DRAFT', 'SENT', 'PENDING_REWORK_RESPONSE', 'REWORK', 'SUBMITTED'].includes(String(record.status || '').toUpperCase()) && (
                                        <button
                                          className='btn btn-sm btn-light-primary'
                                          title='Edit RFQ'
                                          onClick={() => {
                                            void handleEditRFQ(record)
                                          }}
                                        >
                                          Edit
                                        </button>
                                    )}
                                    <button
                                      className='btn btn-sm px-0 procurement-action-icon'
                                      title='View RFQ'
                                      onClick={() => handleViewRFQ(record)}
                                    >
                                      <KTSVG path='/media/map/ph_eye.svg' />
                                    </button>
                                    {canViewVendorLogisticsRequests &&
                                      (pendingLogisticsRequestsByRfqId.get(Number(record.id))?.length || 0) > 0 ? (
                                      <button
                                        className='btn btn-sm btn-warning text-dark'
                                        title='Review Logistics Update'
                                        onClick={() => {
                                          void openLogisticsAction(record)
                                        }}
                                      >
                                        {(pendingLogisticsRequestsByRfqId.get(Number(record.id))?.length || 0) > 1
                                          ? `Review Logistics Updates (${pendingLogisticsRequestsByRfqId.get(Number(record.id))?.length || 0})`
                                          : 'Review Logistics Update'}
                                      </button>
                                    ) : null}
                                    {canDownloadRFQ && (
                                      <button
                                        className='btn btn-sm btn-primary px-2'
                                        title='Download PDF'
                                        onClick={() => {
                                          void handleDownloadRFQ(record)
                                        }}
                                      >
                                        Download
                                      </button>
                                    )}
                                    {canDeleteRFQPermission && (
                                      <button
                                        className='btn btn-sm px-0 procurement-action-icon'
                                        title='Delete'
                                        onClick={() => handleDeleteRFQ(record.id)}
                                      >
                                        <KTSVG path='/media/map/trash.svg' />
                                      </button>
                                    )}
                                  </ProcurementActionGrid>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div
                      className='pagination-wrapper d-flex justify-content-between align-items-center py-3'
                      style={{
                        position: 'static',
                        bottom: 0,
                        backgroundColor: '#fff',
                        zIndex: 10,
                        borderTop: '1px solid #dee2e6',
                        marginTop: 'auto'
                      }}
                    >
                      <div className='d-flex align-items-center'>
                        <span className='text-muted me-2'>Rows per page</span>
                        <SearchableSelect
                          className='form-select'
                          style={{
                            borderRadius: '20px',
                            width: '70px',
                            border: '1px solid #dee2e6',
                            fontSize: '14px',
                            padding: '4px 8px'
                          }}
                          value={rowsPerPage}
                          onChange={handleRowsPerPageChange}
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                        </SearchableSelect>
                      </div>
                      <div className='d-flex align-items-center'>
                        <span className='text-muted me-3' style={{ fontSize: '14px' }}>
                          Showing <strong>{currentRecords.length > 0 ? ((currentPage - 1) * rowsPerPage) + 1 : 0}-{currentRecords.length > 0 ? ((currentPage - 1) * rowsPerPage) + currentRecords.length : 0}</strong> of <strong>{filteredTotalElements}</strong>
                        </span>

                        <nav>
                          <ul className='pagination pagination-sm mb-0' style={{ gap: '2px' }}>
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button
                                className='page-link text-muted'
                                style={{
                                  backgroundColor: '#f8f9fa',
                                  border: '1px solid #dee2e6',
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  borderRadius: '6px'
                                }}
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                              >
                                ‹
                              </button>
                            </li>

                            {(() => {
                              const pages = []
                              const showPages = 5
                              let startPage = Math.max(1, currentPage - 2)
                              let endPage = Math.min(totalPages, startPage + showPages - 1)

                              if (endPage - startPage < showPages - 1) {
                                startPage = Math.max(1, endPage - showPages + 1)
                              }

                              if (startPage > 1) {
                                pages.push(
                                  <li key={1} className='page-item'>
                                    <button
                                      className='page-link text-muted'
                                      style={{
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #dee2e6',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        minWidth: '40px',
                                        borderRadius: '6px'
                                      }}
                                      onClick={() => handlePageChange(1)}
                                    >
                                      1
                                    </button>
                                  </li>
                                )

                                if (startPage > 2) {
                                  pages.push(
                                    <li key='ellipsis1' className='page-item disabled'>
                                      <span className='page-link border-0 text-muted' style={{ backgroundColor: 'transparent', padding: '4px 8px' }}>...</span>
                                    </li>
                                  )
                                }
                              }

                              for (let i = startPage; i <= endPage; i++) {
                                pages.push(
                                  <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                                    <button
                                      className='page-link text-muted'
                                      style={{
                                        backgroundColor: currentPage === i ? '#F4F9FF' : 'transparent',
                                        border: '1px solid #dee2e6',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        minWidth: '40px',
                                        borderRadius: '6px',
                                        outline: 'none',
                                        boxShadow: 'none'
                                      }}
                                      onClick={() => handlePageChange(i)}
                                    >
                                      {i}
                                    </button>
                                  </li>
                                )
                              }

                              if (endPage < totalPages) {
                                if (endPage < totalPages - 1) {
                                  pages.push(
                                    <li key='ellipsis2' className='page-item disabled'>
                                      <span className='page-link border-0 text-muted' style={{ backgroundColor: 'transparent', padding: '4px 8px' }}>...</span>
                                    </li>
                                  )
                                }

                                pages.push(
                                  <li key={totalPages} className='page-item'>
                                    <button
                                      className='page-link text-muted'
                                      style={{
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #dee2e6',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        minWidth: '40px',
                                        borderRadius: '6px'
                                      }}
                                      onClick={() => handlePageChange(totalPages)}
                                    >
                                      {totalPages}
                                    </button>
                                  </li>
                                )
                              }

                              return pages
                            })()}

                            <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                              <button
                                className='page-link text-muted'
                                style={{
                                  backgroundColor: '#f8f9fa',
                                  border: '1px solid #dee2e6',
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  borderRadius: '6px'
                                }}
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages || totalPages === 0}
                              >
                                ›
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ViewRFQModal
        visible={isViewModalVisible}
        onClose={() => {
          setIsViewModalVisible(false)
          setSelectedRFQ(null)
        }}
        rfq={selectedRFQ}
        onDownload={
          canDownloadRFQ
            ? () => {
              if (!selectedRFQ) return
              void handleDownloadRFQ(selectedRFQ)
            }
            : undefined
        }
        onQuotationChanged={() => void refreshSelectedRfqView()}
      />

      <VendorLogisticsChangeRequestModal
        open={activeLogisticsRequest != null}
        request={activeLogisticsRequest}
        canReview={canReviewVendorLogisticsRequests}
        submitting={logisticsActionLoading}
        onClose={() => setActiveLogisticsRequest(null)}
        onApprove={handleApproveLogisticsRequest}
        onReject={handleRejectLogisticsRequest}
      />

      <ProcurementExportModal
        visible={isExportModalVisible}
        onClose={() => setIsExportModalVisible(false)}
        title='Export RFQs'
        documentTypes={['RFQ']}
        defaultDocumentType='RFQ'
        records={exportRecordOptions}
        loading={isExporting}
        allowCompanyFilter={false}
        allowVesselFilter={true}
        allowVendorFilter={false}
        onSubmit={handleRFQExportSubmit}
      />

      {rfqDownloadDoc && (
        <div
          style={{
            position: 'fixed',
            left: '-20000px',
            top: 0,
            width: '1100px',
            opacity: 1,
            pointerEvents: 'none',
            background: '#fff',
          }}
        >
          <div id='rfq-download-host'>
            <QuotationDPCDocument rfq={rfqDownloadDoc} />
          </div>
        </div>
      )}

      {canCreateRFQ && (
        <CreateRFQModal
          visible={isModalVisible}
          onClose={() => {
            setIsModalVisible(false)
            if (isQuickCreate) clearQuickCreateQuery()
          }}
          onSubmit={handleAddRFQSuccess}
          requisitions={requisitions}
          vendors={vendors}
          ports={ports}
          filterVessels={filterVessels}
          mode={isQuickCreate ? 'from-requisition' : 'normal'}
          requisitionId={isQuickCreate ? quickCreateRequisitionId : null}
        />
      )}

      {canEditRFQPermission && (
        <EditRFQModal
          visible={isEditModalVisible}
          onClose={() => {
            setIsEditModalVisible(false)
            setEditingRFQ(null)
          }}
          onSubmit={handleAddRFQSuccess}
          rfq={editingRFQ}
          requisitions={requisitions}
          vendors={vendors}
          ports={ports}
        />
      )}

      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{ top: "5rem" }}
      />
    </div>
  )
}



export default RFQManagement
